import React, { useState } from 'react';
import { Dropdown } from 'primereact/dropdown';

import StatTile from 'shared/components/Dashboard/StatTile';
import MetricCard from 'shared/components/Dashboard/MetricCard';
import AppChart, {
  ChartSeries,
  ChartSlice,
  ChartTool,
  ScatterGroup,
} from 'shared/components/Chart/AppChart';
import ReportDataTable, {
  ReportColumn,
} from 'shared/components/Table/ReportDataTable';
import {
  buildDrilldown,
  DrillField,
  DrillLevel,
  Filters,
  EMPTY_FILTERS,
  drillHint,
  drillLevelLabel,
  drillSearchPlaceholder,
  scopeLabel,
  withUdise,
} from 'shared/json/drilldown';
import DrillBreadcrumb from 'shared/components/Dashboard/DrillBreadcrumb';
import AppIcon from 'shared/components/AppIcon';
import {
  ATT_KPIS,
  ATT_METRICS,
  ATTENDANCE_BANDS,
  SCHOOL_TYPES,
  SCHOOL_TYPE_CHART,
  MARKED_VS_UNMARKED,
  PRESENT_VS_ABSENT,
  ACROSS_PERIODS,
  PERIOD_BREAKDOWN,
  ATT_DISTRICT_SUMMARY,
  DAILY_TREND,
  LOWEST_DISTRICTS,
  ATTENDANCE_BARS,
  ATT_ALARMING_KPIS,
  ATT_ALERTS_BY_CATEGORY,
  ATT_RISK_POINTS,
  POTENTIAL_DROPOUT_BARS,
  ATT_ALERT_DETAILS,
  ATT_ALERT_CATEGORIES,
  ATT_CAUTION_CHECKS,
  ATT_CAUTION_TOTAL,
} from 'shared/json/attendance';

import './AcademicScores.scss';
import useDataVersion from 'shared/hooks/useDataVersion';
import {
  tlmApi,
  useGetAttendanceNumbersQuery,
  useGetAttendanceDailyTrendQuery,
  useGetAttendanceMetricTrendQuery,
  useGetAttendanceCautionSummaryQuery,
  useGetAttendanceCautionListQuery,
  useGetAttendanceDistrictMonthlyQuery,
  useGetAttendanceYearComparisonQuery,
  useGetAttendanceBlockMonthlyQuery,
  useGetAttendanceComparisonQuery,
} from './Dashboard.service';

export interface AttendanceProps {
  activeSubTab?: string;
  district?: string | null;
  block?: string | null;
  school?: string | null;
  /** Numeric ids for the API calls (the names drive the drill-down UI). */
  districtId?: number | string | null;
  blockId?: number | string | null;
  schoolId?: number | string | null;
  /** Selected academic year — sent on every API call. */
  acdYr?: number | null;
  filters?: Filters;
  onDistrictChange?: (value: string) => void;
  onBlockChange?: (value: string) => void;
  onSchoolChange?: (value: string) => void;
  /**
   * Focus the dashboard on one school, setting District → Block → School
   * together. Used by the Caution flagged-school table, whose rows are
   * already at school level and so cannot drill any deeper.
   */
  onGeoSelect?: (geo: {
    district?: string | null;
    block?: string | null;
    school?: string | null;
  }) => void;
}

const fmtIndian = (n: number) => n.toLocaleString('en-IN');
const scopeText =
  'Government, Partially Aided & Fully Aided schools only. As of 2026-09-24. Compliance = marked / expected school-days. Potential dropout = absent 15+ days in the month.';

const RISK_COLORS: Record<string, string> = {
  critical: '#c0392b',
  'under-reporting': '#7e57c2',
  'under-performing': '#e8a33d',
  healthy: '#5cb85c',
};
const RISK_LABELS: Record<string, string> = {
  critical: 'Critical',
  'under-reporting': 'Under-reporting',
  'under-performing': 'Under-performing',
  healthy: 'Healthy',
};

const chartToolsNoTable: ChartTool[] = [
  { icon: 'pi pi-refresh', label: 'Refresh' },
  { icon: 'pi pi-download', label: 'Download' },
];

// Combo chart toolbar with Bar / Line view toggles + Table View (auto-wired by
// label).
const chartToolsCombo: ChartTool[] = [
  { icon: 'pi pi-chart-bar', label: 'Bar view' },
  { icon: 'pi pi-chart-line', label: 'Line view' },
  { icon: 'pi pi-refresh', label: 'Refresh' },
  { icon: 'pi pi-file', label: 'Table View' },
  { icon: 'pi pi-download', label: 'Download' },
];

const periodOptions = [
  { label: 'Last 3 days', value: 'last3' },
  { label: 'Last 5 days', value: 'last5' },
  { label: 'Last 7 days', value: 'last7' },
  { label: 'Last 15 days', value: 'last15' },
  { label: 'Last 30 days', value: 'last30' },
];

// Period selection → API `flag`: 1=3d, 2=5d, 3=7d, 4=15d, 5=30d.
const PERIOD_FLAG: Record<string, number> = {
  last3: 1,
  last5: 2,
  last7: 3,
  last15: 4,
  last30: 5,
};

// Period selection → number of days (for dynamic labels).
const PERIOD_DAYS: Record<string, number> = {
  last3: 3,
  last5: 5,
  last7: 7,
  last15: 15,
  last30: 30,
};

const metricOptions = [
  { label: 'Student Attendance', value: 1 },
  { label: 'Marking Compliance', value: 2 },
  { label: 'Teacher Attendance', value: 3 },
  { label: 'Dropout Risk Rate', value: 4 },
];

// "SHOW N" selector for the Lowest/Highest districts line chart.
const showOptions = [
  { label: '10', value: 10 },
  { label: '12', value: 12 },
  { label: '15', value: 15 },
  { label: '20', value: 20 },
  { label: '38', value: 38 },
];

// Academic-calendar months (Jun → May), value = calendar month number.
// Used by the Comparison > Between Years month-window selectors.
const MONTH_OPTIONS = [
  { label: 'Jun', value: 6 },
  { label: 'Jul', value: 7 },
  { label: 'Aug', value: 8 },
  { label: 'Sep', value: 9 },
  { label: 'Oct', value: 10 },
  { label: 'Nov', value: 11 },
  { label: 'Dec', value: 12 },
  { label: 'Jan', value: 1 },
  { label: 'Feb', value: 2 },
  { label: 'Mar', value: 3 },
  { label: 'Apr', value: 4 },
  { label: 'May', value: 5 },
];

// Every metric the Comparison tab needs, requested in a single call.
// attendancecomparison serves metrics 1/2/4 from one student-table scan and
// metric 3 from one teacher-table scan, so asking for all four costs the same
// two scans as asking for one.
const ALL_COMPARISON_METRICS = '1,2,3,4';

// Every caution category (1=Zero Marking, 2=Marking Under 25%,
// 3=Zero Attendance, 4=Dropout Risk Over 20%), requested in a single call so
// switching caution tiles costs no requests.
const ALL_CAUTION_TYPES = '1,2,3,4';

// Pull one metric's section out of a multi-metric attendancecomparison
// response, merged with the shared top-level fields (flag, period, days,
// from_date, to_date, level, group_key) so the result has exactly the same
// shape the old single-metric request returned. Module-level so the identity is
// stable and it can be used inside useMemo without being a dependency.
const pickMetricSection = (
  root: unknown,
  metric: number | string
): Record<string, unknown> | undefined => {
  const o = (root ?? {}) as Record<string, unknown>;
  const map = o.by_metric as
    Record<string, Record<string, unknown>> | undefined;
  const section = map?.[String(metric)];
  if (!section) return undefined;
  const base = { ...o };
  delete base.by_metric;
  delete base.metrics;
  return { ...base, ...section };
};

// Parse the START year from a label like '2024-25' -> 2024 (acd_yr ids are
// not chronological, so the label is the reliable sort key).
const startYearOf = (name: string): number =>
  Number(String(name).match(/(\d{4})/)?.[1] ?? NaN);

// Single-day options: yesterday back through exactly 30 days (yesterday first).
const buildSingleDayOptions = () => {
  const opts: { label: string; value: string }[] = [];
  const d = new Date();
  d.setDate(d.getDate() - 1); // start at yesterday
  for (let i = 0; i < 30; i++) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const value = `${yyyy}-${mm}-${dd}`;
    opts.push({ label: value, value });
    d.setDate(d.getDate() - 1);
  }
  return opts;
};
const SINGLE_DAY_OPTIONS = buildSingleDayOptions();

const Attendance: React.FC<AttendanceProps> = ({
  activeSubTab = 'numbers',
  district = null,
  block = null,
  school = null,
  districtId = null,
  blockId = null,
  schoolId = null,
  acdYr = null,
  filters = EMPTY_FILTERS,
  onDistrictChange,
  onBlockChange,
  onSchoolChange,
  // Used ONLY by the Caution tab's flagged-school row click, which jumps
  // straight to a school and needs District + Block set together (the row
  // already carries its own district/block names) so the header dropdowns
  // sync and the drill breadcrumb — which only renders once a district is
  // set — appears above the grid.
  onGeoSelect,
}) => {
  const [period, setPeriod] = useState('last30');
  // Clicking a school row SELECTS that school (sets the header), which narrows
  // this whole tab — its comparison charts + grids — to that one school. No
  // detail card is opened. Strips any trailing " (udise)" so the dropdown
  // value matches.
  const selectSchool = (name: string) => {
    const bare = name.replace(/\s*\(\d[\d/]*\)\s*$/, '').trim();
    if (bare) onSchoolChange?.(bare);
  };
  // When a school is selected in the header, narrow an entity array (schools of
  // the block) to just that one school so every comparison chart + table shows
  // a single entity in sync with the selection. Matches on the bare name (API
  // rows carry no UDISE suffix here). Falls back to the full list if nothing
  // matches, so a name mismatch never blanks the view.
  const narrowToSchool = <T,>(rows: T[], name: (r: T) => unknown): T[] => {
    if (!school) return rows;
    const want = school.trim().toLowerCase();
    const strip = (s: string) =>
      s
        .replace(/\s*\(\d[\d/]*\)\s*$/, '')
        .trim()
        .toLowerCase();
    const filtered = rows.filter((r) => strip(String(name(r) ?? '')) === want);
    return filtered.length > 0 ? filtered : rows;
  };
  const [dayMode, setDayMode] = useState<'period' | 'single'>('period');
  // Default to yesterday (the first single-day option).
  const [singleDate, setSingleDate] = useState(
    SINGLE_DAY_OPTIONS[0]?.value ?? ''
  );
  const [trendMetric, setTrendMetric] = useState(1);
  const [trendMode, setTrendMode] = useState<'lowest' | 'highest'>('lowest');
  // How many districts to show in the Lowest/Highest line chart.
  const [trendShow, setTrendShow] = useState(12);
  const [comparisonMetric, setComparisonMetric] = useState(1);
  // Between-Days: the two dates being compared (DAY A vs DAY B).
  const [dayA, setDayA] = useState<string | null>(null);
  const [dayB, setDayB] = useState<string | null>(null);
  const [alarmFilter, setAlarmFilter] = useState('all');
  const [cautionCheck, setCautionCheck] = useState('zero_marking');
  // Comparison sub-tab view: districts | days | years.
  const [comparisonView, setComparisonView] = useState<
    'districts' | 'days' | 'years'
  >('districts');
  // Between-Years selectors (acd_yr ids + academic month window).
  const [fromYear, setFromYear] = useState<number | null>(null);
  const [toYear, setToYear] = useState<number | null>(null);
  const [monthFrom, setMonthFrom] = useState<number | null>(null);
  const [monthTo, setMonthTo] = useState<number | null>(null);

  // ── API arguments derived from the current selection ──
  // edate only in single-day mode; district_id / block_id from the header.
  // Single-day mode → edate; period mode → flag (1=3d, 2=5d, 3=7d, 4=15d, 5=30d).
  // Exactly one of edate / flag is always sent (default flag=5 on load).
  const edate = dayMode === 'single' ? singleDate : undefined;
  const flag = dayMode === 'period' ? PERIOD_FLAG[period] : undefined;
  // Human label for the active window — used in metric-card labels.
  const periodSuffix =
    dayMode === 'single' ? singleDate : `Last ${PERIOD_DAYS[period] ?? 30}d`;
  // Current geography scope + working-days window for section headings. Every
  // heading below uses scopeName rather than a hardcoded state name, so a
  // title never claims "Tamil Nadu" while the figures are for one district.
  const scopeName = scopeLabel({ district, block, school });
  const workingDaysLabel =
    dayMode === 'single'
      ? `As of ${singleDate}`
      : `Last ${PERIOD_DAYS[period] ?? 30} working days`;
  // Nightly data generation stamp. Carried in every query's args so the client
  // cache is scoped to the generation it was built from: stable all day (no
  // refetching), and rolled over automatically when the batch publishes new
  // data. Queries wait for it (`skip: !dataVersion`) so each generation has a
  // single canonical cache key.
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const dataVersion = d.toISOString().split('T')[0];
  const awaitingVersion = !dataVersion;

  // The first selected value of each cross-cutting filter dimension.
  const mgmtFilter = filters.management[0];
  const typeFilter = filters.schoolType[0];
  const cateFilter = filters.category[0];

  // NOTE: acd_yr is intentionally NOT sent for any Attendance API — the
  // attendance dashboard is not scoped by academic year.
  // Memoised so the object identity only changes when the scope actually
  // changes: these args drive the background-prefetch effect's dependencies.
  const periodArgs = React.useMemo(
    () => ({
      ...(edate ? { edate } : {}),
      ...(flag != null ? { flag } : {}),
      ...(mgmtFilter ? { management_id: mgmtFilter } : {}),
      ...(typeFilter ? { school_type: typeFilter } : {}),
      ...(cateFilter ? { cate_id: cateFilter } : {}),
      ...(districtId != null && districtId !== ''
        ? { district_id: districtId }
        : {}),
      ...(blockId != null && blockId !== '' ? { block_id: blockId } : {}),
      ...(schoolId != null && schoolId !== '' ? { school_id: schoolId } : {}),
      ...(dataVersion ? { v: dataVersion } : {}),
    }),
    [
      edate,
      flag,
      mgmtFilter,
      typeFilter,
      cateFilter,
      districtId,
      blockId,
      schoolId,
      dataVersion,
    ]
  );
  // Numbers (Summary) sub-tab data — only fetched while that tab is open, and
  // only once the data generation stamp is known.
  const skipNumbers = activeSubTab !== 'numbers' || awaitingVersion;
  // Single combined call for the whole Numbers tab. One request (one cache
  // entry on the backend) returns every section the tab renders, replacing the
  // 6-8 separate calls that used to fire on each filter change:
  //   result.summary        -> KPI tiles + marked/present donuts (was attendancesummary)
  //   result.bands          -> attendance band distribution (was attendancebands)
  //   result.by_school_type -> by school type chart/table (was attendancebyschooltype)
  //   result.across_periods -> periods combo chart/table (was attendanceacrossperiods)
  //   result.daily_trend    -> district_wise table source (was attendancedailytrend)
  const { data: numbersData, isFetching: numbersLoading } =
    useGetAttendanceNumbersQuery(periodArgs, { skip: skipNumbers });
  const numbers = numbersData as
    | {
        summary?: unknown;
        bands?: unknown;
        by_school_type?: unknown;
        by_district?: unknown;
        across_periods?: unknown;
      }
    | undefined;

  // Per-section data derived from the single response. Every Numbers section
  // now shares one loading flag (they all arrive together).
  const summaryData = numbers?.summary;
  const bandsData = numbers?.bands;
  const schoolTypeData = numbers?.by_school_type;
  const byDistrictData = numbers?.by_district;
  const acrossPeriodsData = numbers?.across_periods;
  const summaryLoading = numbersLoading;
  const bandsLoading = numbersLoading;
  const schoolTypeLoading = numbersLoading;
  const acrossLoading = numbersLoading;

  // Daily trend — fetched standalone only for the Trend Analysis sub-tab. It is
  // NOT part of the Numbers tab (the Numbers district table reads by_district),
  // so it stays a separate call that fires only when the Trend tab is open.
  const { data: dailyTrendData, isFetching: dailyLoading } =
    useGetAttendanceDailyTrendQuery(periodArgs, {
      skip: activeSubTab !== 'trend' || awaitingVersion,
    });

  const { data: MetricTrendData, isFetching: metricLoading } =
    useGetAttendanceMetricTrendQuery(
      { ...periodArgs, metric: trendMetric },
      {
        skip: activeSubTab !== 'trend' || awaitingVersion,
      }
    );
  const { data: CautionSummaryData, isFetching: cautionSummaryLoading } =
    useGetAttendanceCautionSummaryQuery(periodArgs, {
      skip: activeSubTab !== 'caution' || awaitingVersion,
    });
  //console.log(CautionSummaryData, 'CautionSummaryData');

  // Resolve the active caution tile's numeric `type` from the current
  // selection (cautionCheck holds the API tile `key`). Defaults to the
  // first tile / 1 until the summary data is available.
  const cautionTiles: Array<{ type: string | number; key: string }> =
    Array.isArray(CautionSummaryData?.tiles) ? CautionSummaryData.tiles : [];
  const activeCautionType =
    cautionTiles.find((t) => t.key === cautionCheck)?.type ??
    cautionTiles[0]?.type ??
    1;

  // Every caution category's school list in ONE request. This used to refetch
  // per selected tile (one call per category as the user clicked around); now
  // all categories arrive together and switching tiles needs no network. The
  // backend also resolves the shared dropout-school set once instead of per
  // request.
  const { data: CautionListAll, isFetching: cautionListLoading } =
    useGetAttendanceCautionListQuery(
      { ...periodArgs, types: ALL_CAUTION_TYPES },
      { skip: activeSubTab !== 'caution' || awaitingVersion }
    );
  // The selected tile's section, sliced out of that response. Same shape the
  // single-type response had, so the render code below is unchanged.
  const CautionListData = React.useMemo(() => {
    const byType = (
      CautionListAll as
        { by_type?: Record<string, { schools?: unknown[] }> } | undefined
    )?.by_type;
    return byType?.[String(activeCautionType)];
  }, [CautionListAll, activeCautionType]);

  // Comparison tab (attendancecomparison). districts mode = ranked per-district
  // values; days mode = day-wise groups (grouped by the header district/block).
  // ── Comparison tab (attendancecomparison) ──────────────────────
  // The tab needs every metric at once (the Full Comparison table merges all
  // four per district; the Between-Days cards need attendance + compliance +
  // teacher). This used to mean 9 separate requests to the SAME endpoint —
  // one per metric per view, plus one for the selected metric. Now each view
  // issues ONE multi-metric request (metrics=1,2,3,4) and every per-metric
  // dataset below is read out of that single response.
  const cmpArgsBase = React.useMemo(
    () => ({
      // Single-day mode → send edate; period mode → send flag.
      ...(dayMode === 'single'
        ? { edate: singleDate }
        : { flag: PERIOD_FLAG[period] ?? 5 }),
      ...(districtId != null && districtId !== ''
        ? { district_id: districtId }
        : {}),
      ...(blockId != null && blockId !== '' ? { block_id: blockId } : {}),
      ...(mgmtFilter ? { manage_id: mgmtFilter } : {}),
      ...(typeFilter ? { school_type_id: typeFilter } : {}),
      ...(cateFilter ? { cate_id: cateFilter } : {}),
      ...(dataVersion ? { v: dataVersion } : {}),
    }),
    [
      dayMode,
      singleDate,
      period,
      districtId,
      blockId,
      mgmtFilter,
      typeFilter,
      cateFilter,
      dataVersion,
    ]
  );

  // Between Districts view — one value per district for each metric.
  const { data: DistrictsAll, isFetching: districtsCmpLoading } =
    useGetAttendanceComparisonQuery(
      {
        ...cmpArgsBase,
        comparisontab: 'districts',
        metrics: ALL_COMPARISON_METRICS,
      },
      {
        skip:
          activeSubTab !== 'comparison' ||
          comparisonView !== 'districts' ||
          awaitingVersion,
      }
    );

  // Between Days view — day-wise groups for each metric.
  const { data: DaysAll, isFetching: daysCmpLoading } =
    useGetAttendanceComparisonQuery(
      {
        ...cmpArgsBase,
        comparisontab: 'days',
        metrics: ALL_COMPARISON_METRICS,
      },
      {
        skip:
          activeSubTab !== 'comparison' ||
          comparisonView !== 'days' ||
          awaitingVersion,
      }
    );

  // Per-metric datasets, sliced out of the two responses above. Memoised so the
  // object identities stay stable across renders (ComparisonData feeds an
  // effect dependency list).
  const FullAttendance = React.useMemo(
    () => pickMetricSection(DistrictsAll, 1),
    [DistrictsAll]
  );
  const FullCompliance = React.useMemo(
    () => pickMetricSection(DistrictsAll, 2),
    [DistrictsAll]
  );
  const FullTeacher = React.useMemo(
    () => pickMetricSection(DistrictsAll, 3),
    [DistrictsAll]
  );
  const FullDropout = React.useMemo(
    () => pickMetricSection(DistrictsAll, 4),
    [DistrictsAll]
  );
  const DaysAttendance = React.useMemo(
    () => pickMetricSection(DaysAll, 1),
    [DaysAll]
  );
  const DaysCompliance = React.useMemo(
    () => pickMetricSection(DaysAll, 2),
    [DaysAll]
  );
  const DaysTeacher = React.useMemo(
    () => pickMetricSection(DaysAll, 3),
    [DaysAll]
  );

  // The currently-selected metric's payload (drives the ranked bar chart and
  // the day-wise chart). Derived from the active view's response — no separate
  // request, since the selected metric is always one of the four fetched.
  const ComparisonData = React.useMemo(
    () =>
      comparisonView === 'days'
        ? pickMetricSection(DaysAll, comparisonMetric)
        : comparisonView === 'districts'
          ? pickMetricSection(DistrictsAll, comparisonMetric)
          : undefined,
    [comparisonView, comparisonMetric, DaysAll, DistrictsAll]
  );
  const comparisonLoading =
    comparisonView === 'days' ? daysCmpLoading : districtsCmpLoading;

  // ── Background prefetch of the sibling sub-tabs ────────────────
  // The data is immutable for the day, so a prefetch is never wasted work and
  // never goes stale. Once the active sub-tab has had a moment to render, pull
  // the other sub-tabs' payloads into the cache so switching to them is
  // instant instead of showing a spinner. Deliberately delayed so it never
  // competes with the request the user is actually waiting on.
  const prefetchNumbers = tlmApi.usePrefetch('getAttendanceNumbers');
  const prefetchDailyTrend = tlmApi.usePrefetch('getAttendanceDailyTrend');
  const prefetchComparison = tlmApi.usePrefetch('getAttendanceComparison');

  React.useEffect(() => {
    if (awaitingVersion) return;
    const timer = setTimeout(() => {
      // Whichever sub-tab is open, warm the other two heavy ones.
      if (activeSubTab !== 'numbers') prefetchNumbers(periodArgs);
      if (activeSubTab !== 'trend') prefetchDailyTrend(periodArgs);
      if (activeSubTab !== 'comparison') {
        prefetchComparison({
          ...cmpArgsBase,
          comparisontab: 'districts',
          metrics: ALL_COMPARISON_METRICS,
        });
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [
    activeSubTab,
    awaitingVersion,
    periodArgs,
    cmpArgsBase,
    prefetchNumbers,
    prefetchDailyTrend,
    prefetchComparison,
  ]);

  // Default DAY A / DAY B to the last two available days once data loads.
  React.useEffect(() => {
    const dd = (ComparisonData as { days_data?: { edate?: string }[] })
      ?.days_data;
    if (Array.isArray(dd) && dd.length && (dayA == null || dayB == null)) {
      const dates = dd.map((d) => String(d.edate ?? ''));
      setDayA(dates[dates.length - 2] ?? dates[0]);
      setDayB(dates[dates.length - 1]);
    }
  }, [ComparisonData, dayA, dayB]);

  // ── Between Years ─────────────────────────────────────────────
  // Shared scope (district / block / month window) for the year endpoints.
  const yearArgs = React.useMemo(
    () => ({
      ...(districtId != null && districtId !== ''
        ? { district_id: districtId }
        : {}),
      ...(blockId != null && blockId !== '' ? { block_id: blockId } : {}),
      ...(monthFrom != null && monthTo != null
        ? { month_from: monthFrom, month_to: monthTo }
        : {}),
      // Classification filters, same names the comparison endpoint uses
      ...(mgmtFilter ? { manage_id: mgmtFilter } : {}),
      ...(typeFilter ? { school_type_id: typeFilter } : {}),
      ...(cateFilter ? { cate_id: cateFilter } : {}),
      ...(dataVersion ? { v: dataVersion } : {}),
    }),
    [
      districtId,
      blockId,
      monthFrom,
      monthTo,
      mgmtFilter,
      typeFilter,
      cateFilter,
      dataVersion,
    ]
  );
  const skipYearsTab =
    activeSubTab !== 'comparison' ||
    comparisonView !== 'years' ||
    awaitingVersion;

  // attendancedistrictmonthly — fired as soon as the Between-Years tab opens
  // (NO from/to on first load). Its result.academic_years lists exactly the
  // years that have data, which drives the YEAR A / YEAR B + month selectors.
  const { data: DistrictMonthlyData } = useGetAttendanceDistrictMonthlyQuery(
    {
      ...(fromYear != null ? { from_year: fromYear } : {}),
      ...(toYear != null ? { to_year: toYear } : {}),
      ...yearArgs,
    },
    { skip: skipYearsTab }
  );

  // attendanceyearcomparison?year_a=&year_b= — needs both years selected.
  const { data: YearComparisonData, isFetching: yearLoading } =
    useGetAttendanceYearComparisonQuery(
      {
        ...(fromYear != null ? { year_a: fromYear } : {}),
        ...(toYear != null ? { year_b: toYear } : {}),
        ...yearArgs,
      },
      { skip: skipYearsTab || fromYear == null }
    );

  // attendanceblockmonthly — needs a district scope to return block rows.
  const { data: BlockMonthlyData } = useGetAttendanceBlockMonthlyQuery(
    {
      ...(fromYear != null ? { from_year: fromYear } : {}),
      ...(toYear != null ? { to_year: toYear } : {}),
      ...yearArgs,
    },
    { skip: skipYearsTab || !(districtId != null && districtId !== '') }
  );
  //console.log(YearComparisonData, 'YearComparisonData', {
  //   year_a: fromYear,
  //   year_b: toYear,
  //   ...yearArgs,
  // });
  //console.log(DistrictMonthlyData, 'DistrictMonthlyData');
  //console.log(BlockMonthlyData, 'BlockMonthlyData');

  // Academic-year options from the monthly endpoint's academic_years (only
  // the years that actually have data). value = academic_year_id.
  const academicYearsMeta = React.useMemo(() => {
    const ays = (
      DistrictMonthlyData as {
        academic_years?: {
          academic_year_id?: string | number;
          academic_year?: string;
          months?: (string | number)[];
        }[];
      }
    )?.academic_years;
    return Array.isArray(ays) ? ays : [];
  }, [DistrictMonthlyData]);

  const yearOptions = React.useMemo(
    () =>
      academicYearsMeta
        .filter((y) => y?.academic_year_id != null)
        .map((y) => ({
          label: String(y.academic_year ?? y.academic_year_id),
          value: Number(y.academic_year_id),
        }))
        .sort((a, b) => startYearOf(a.label) - startYearOf(b.label)),
    [academicYearsMeta]
  );

  // Month options from the months present in the meta (academic order).
  const yearMonthOptions = React.useMemo(() => {
    const present = new Set<number>();
    academicYearsMeta.forEach((y) =>
      (y.months ?? []).forEach((m) => present.add(Number(m)))
    );
    return present.size
      ? MONTH_OPTIONS.filter((o) => present.has(o.value))
      : MONTH_OPTIONS;
  }, [academicYearsMeta]);

  // Default YEAR A = earliest year with data, YEAR B = latest, once meta loads.
  React.useEffect(() => {
    if (yearOptions.length && fromYear == null && toYear == null) {
      setFromYear(yearOptions[0].value);
      setToYear(yearOptions[yearOptions.length - 1].value);
    }
  }, [yearOptions, fromYear, toYear]);

  // YEAR A and YEAR B cannot be the same, so each list excludes the other's
  // current selection. Months are only offered once both years are chosen.
  const yearAOptions = yearOptions.filter((o) => o.value !== toYear);
  const yearBOptions = yearOptions.filter((o) => o.value !== fromYear);
  const bothYearsSelected = fromYear != null && toYear != null;
  // ── Resolve API values with dummy fallback (until every field is mapped) ──
  // `summaryData` / `bandsData` are already the `.result` objects.
  const summary = summaryData as
    | {
        student?: {
          attendance_pct?: number;
          absent?: string | number;
          schools_marked?: number;
          total?: string;
          present?: string;
        };
        teacher?: { attendance_pct?: number };
        present_vs_absent?: { present_pct?: number; absent_pct?: number };
        marking_compliance_pct?: number;
        dropout_risk_pct?: number;
        unmarked_schools?: string;
        potential_dropouts?: string;
        days?: string;
        total_students?: string;
      }
    | undefined;
  const bands = bandsData as
    | {
        total_schools?: string | number;
        bands?: {
          band: string;
          schools: string | number;
          share_pct: number;
          students: string | number;
          students_share_pct: number;
        }[];
      }
    | undefined;

  const studentAttendancePct =
    summary?.student?.attendance_pct ?? ATT_KPIS.studentAttendance;
  const studentCompliansePct = summary?.marking_compliance_pct;
  const studentPotentialDropoutRiskPct = summary?.dropout_risk_pct;
  const unmarked_schools =
    Number(summary?.unmarked_schools) * Number(summary?.days);
  const potential_dropouts = Number(summary?.potential_dropouts);
  const selected_days = Number(summary?.days);
  const teacherAttendancePct =
    summary?.teacher?.attendance_pct ?? ATT_KPIS.teacherAttendance;
  const presentPct =
    summary?.present_vs_absent?.present_pct ?? PRESENT_VS_ABSENT.present;
  const absentPct =
    summary?.present_vs_absent?.absent_pct ?? PRESENT_VS_ABSENT.absent;
  const absenteeDaysVal =
    summary?.student?.absent != null
      ? Number(summary.student.absent)
      : ATT_METRICS.absenteeDays;
  const schoolsVal =
    summary?.student?.schools_marked != null
      ? Number(summary?.student?.schools_marked)
      : ATT_METRICS.schools;
  const totalStudents = Number(summary?.total_students);
  const absenteeDays = Number(summary?.student?.absent);
  // Present / absent student counts (for the Present vs Absent table view).
  const presentStudents = Number(summary?.student?.present);
  const absentStudents =
    summary?.student?.absent != null ? Number(summary.student.absent) : NaN;
  // School types — show only the first 5 (Government, Fully Aided, Un-aided,
  // Partially Aided, Central Govt); the rest are "Unknown" buckets.
  const schoolTypesApi = (
    schoolTypeData as
      | {
          school_types?: {
            school_type_id: string;
            school_type: string;
            schools: string | number;
            student_present: string | number;
            student_absent: string | number;
            student_attendance_pct: number;
            teacher_attendance_pct: number;
            marking_compliance_pct: number;
            dropout_risk_pct: number;
          }[];
        }
      | undefined
  )?.school_types;
  //console.log(schoolTypesApi, 'schoolTypesApi');
  const schoolTypeTop = Array.isArray(schoolTypesApi)
    ? schoolTypesApi.filter(
        (s) => Number(s.school_type_id) !== 3 && Number(s.school_type_id) !== 5
      )
    : null;
  // Attendance-band rows (API lacks student counts, so those stay as "—").
  const bandRows = Array.isArray(bands?.bands)
    ? bands!.bands!.map((b, i) => ({
        band: b.band,
        schools: Number(b.schools),
        shareSchools: `${b.share_pct}%`,
        students: Number(b.students),
        shareStudents: `${b.students_share_pct}%`,
        color: ATTENDANCE_BANDS[i]?.color ?? '#8bc34a',
      }))
    : ATTENDANCE_BANDS;

  // Attendance across periods (API). Compliance / unmarked aren't in this
  // payload, so the tooltip shows "no_data" for them.
  type AcrossPeriodRow = {
    period: string;
    student_attendance_pct: number;
    absentee_days: string | number;
    marking_compliance_pct: number;
    unmarked_schools: string;
  };
  const acrossRaw = acrossPeriodsData as
    | AcrossPeriodRow[]
    | { periods?: AcrossPeriodRow[]; across_periods?: AcrossPeriodRow[] }
    | undefined;
  const acrossList: AcrossPeriodRow[] = Array.isArray(acrossRaw)
    ? acrossRaw
    : Array.isArray(acrossRaw?.periods)
      ? acrossRaw!.periods!
      : Array.isArray(acrossRaw?.across_periods)
        ? acrossRaw!.across_periods!
        : [];
  const periodLabels = acrossList.length
    ? acrossList.map((p) => `Last ${p.period}`)
    : ACROSS_PERIODS.periods;
  const periodAttendance = acrossList.length
    ? acrossList.map((p) => p.student_attendance_pct)
    : ACROSS_PERIODS.attendancePct;
  const periodAbsentee = acrossList.length
    ? acrossList.map((p) => Number(p.absentee_days))
    : ACROSS_PERIODS.absenteeDays;
  const periodsTooltip: Record<string, { label: string; value: string }[]> = {};
  if (acrossList.length) {
    // Safe formatters: show "No data" for missing/empty fields instead of
    // rendering a bare "%" or "NaN" in the tooltip.
    const pctOrNA = (v: unknown) =>
      v == null || v === '' || Number.isNaN(Number(v)) ? '0' : `${v}%`;
    const numOrNA = (v: unknown) =>
      v == null || v === '' || Number.isNaN(Number(v))
        ? '0'
        : fmtIndian(Number(v));
    acrossList.forEach((p) => {
      periodsTooltip[`Last ${p.period}`] = [
        { label: 'Attendance', value: pctOrNA(p.student_attendance_pct) },
        { label: 'Absentee-days', value: numOrNA(p.absentee_days) },
        { label: 'Compliance', value: pctOrNA(p.marking_compliance_pct) },
        {
          label: 'Unmarked school-days',
          value: numOrNA(
            (Number(p.unmarked_schools) || 0) * (parseInt(p.period, 10) || 0)
          ),
        },
      ];
    });
  } else {
    // No API data → mirror the dummy bars so the tooltip still shows values
    // (keys must match the chart labels in ACROSS_PERIODS.periods).
    ACROSS_PERIODS.periods.forEach((label, i) => {
      periodsTooltip[label] = [
        {
          label: 'Attendance',
          value: `${ACROSS_PERIODS.attendancePct[i] ?? 0}%`,
        },
        {
          label: 'Absentee-days',
          value: fmtIndian(Number(ACROSS_PERIODS.absenteeDays[i] ?? 0)),
        },
      ];
    });
  }

  // Period Breakdown table rows (compliance / unmarked not in payload → no_data).
  const periodBreakdownRows: Record<string, unknown>[] = acrossList.length
    ? acrossList.map((p) => ({
        period: `Last ${p.period} working days`,
        attendance: `${p.student_attendance_pct}%`,
        absenteeDays: Number(p.absentee_days),
        compliance: `${p.marking_compliance_pct}%`,
        unmarkedDays: `${(Number(p.unmarked_schools) || 0) * (parseInt(p.period, 10) || 0)}`,
      }))
    : PERIOD_BREAKDOWN;

  // Shared "no data" formatters used across the Numbers / Comparison / Caution
  // tables: render missing values as 'No data' instead of 0 / '0%'.
  const isMissing = (v: unknown): boolean =>
    v === null || v === undefined || v === '';
  const ndText = (v: unknown): string => (isMissing(v) ? '0' : String(v));
  const ndPct = (v: unknown): string => (isMissing(v) ? '0' : `${v}%`);
  const ndNum = (v: unknown): number | string =>
    isMissing(v) ? '0' : Number(v);

  // Districts — Attendance Summary base rows. Primary source is the combined
  // response's by_district section. Falls back to dummy data while loading /
  // on error.
  //
  // by_district's actual shape (confirmed against the live API) is NOT the
  // generic { level, rows } envelope every other drill-down endpoint in this
  // app uses — it nests the child level one level deeper, keyed by "_detail":
  //   by_district.districts            -> district rows (state level, or just
  //                                        the one selected district's own
  //                                        totals once scoped — never its
  //                                        children)
  //   by_district.district_detail.blocks -> the selected district's blocks
  //                                          (present only when district_id
  //                                          was sent)
  //   by_district.block_detail.schools   -> the selected block's schools
  //                                          (present only when block_id was
  //                                          sent)
  // Reading `districts` alone at every scope (as if it always held the next
  // level down) meant the block/school rows were never found — this table
  // fell straight to buildDrilldown's SYNTHESISED names (e.g. "ARIYALUR
  // Central / Extension / Urban…") the moment a district or block was
  // selected, even though the real rows had already arrived in
  // district_detail / block_detail.
  //
  // Using the API's own names matters for header sync: the District / Block /
  // School dropdowns are built from the master list APIs, so only a real API
  // name matches a dropdown option. buildDrilldown's synthesised child names
  // (e.g. "Chennai Urban") never would, which is why block/school row clicks
  // left the dropdowns blank.
  type AttendanceApiRow = {
    district_id?: string | number;
    block_id?: string | number;
    school_id?: string | number;
    district_name?: string;
    block_name?: string;
    school_name?: string;
    name?: string;
    student_present?: string | number;
    student_absent?: string | number;
    total_students?: string | number;
    student_attendance_pct?: number;
    teacher_attendance_pct?: number;
    // Only present on district / block rows — schools carry no compliance or
    // dropout figures of their own.
    marking_compliance_pct?: number;
    dropout_risk_pct?: number;
    /** Head-count behind dropout_risk_pct (absent 15+ days in the month). */
    potential_dropouts?: string | number;
  };

  const byDistrictObj = (byDistrictData ?? {}) as {
    districts?: AttendanceApiRow[];
    district_detail?: {
      district_id?: string | number;
      district_name?: string;
      blocks?: AttendanceApiRow[];
    };
    block_detail?: {
      summary?: unknown;
      schools?: AttendanceApiRow[];
    };
  };

  // Pick the rows for the level the current selection implies, straight from
  // where the API actually puts them. blockId selected -> that block's
  // schools; districtId selected (no block) -> that district's blocks;
  // neither -> the state-level district rows.
  const apiScoped: { level: DrillLevel; rows: AttendanceApiRow[] } | null =
    blockId != null && blockId !== ''
      ? Array.isArray(byDistrictObj.block_detail?.schools) &&
        byDistrictObj.block_detail!.schools!.length > 0
        ? { level: 2, rows: byDistrictObj.block_detail!.schools! }
        : null
      : districtId != null && districtId !== ''
        ? Array.isArray(byDistrictObj.district_detail?.blocks) &&
          byDistrictObj.district_detail!.blocks!.length > 0
          ? { level: 1, rows: byDistrictObj.district_detail!.blocks! }
          : null
        : Array.isArray(byDistrictObj.districts) &&
            byDistrictObj.districts.length > 0
          ? { level: 0, rows: byDistrictObj.districts }
          : null;

  const toSummaryRow = (d: AttendanceApiRow): Record<string, unknown> => ({
    name: String(
      d.district_name ?? d.block_name ?? d.school_name ?? d.name ?? ''
    ),
    attendance: ndPct(d.student_attendance_pct),
    // School rows carry no compliance / dropout figures — ndText/ndPct show
    // 'No data' instead of a literal "undefined%".
    compliance: ndPct(d.marking_compliance_pct),
    teacher: ndPct(d.teacher_attendance_pct),
    absenteeDays: Number(d.student_absent ?? 0),
    // Head-count of at-risk students, alongside the percentage. ndNum keeps a
    // missing value as 'No data' rather than rendering it as 0.
    dropouts: ndNum(d.potential_dropouts),
    dropoutRisk: ndPct(d.dropout_risk_pct),
    district_id: d.district_id,
    block_id: d.block_id,
    school_id: d.school_id,
  });

  /** Level the API actually returned rows for, or null when it returned none. */
  const apiRowLevel: DrillLevel | null = apiScoped?.level ?? null;
  const districtBaseRows: Record<string, unknown>[] = apiScoped
    ? apiScoped.rows.map(toSummaryRow)
    : ATT_DISTRICT_SUMMARY;
  // Switch mode and reset the relevant selection to its default (done in the
  // handler rather than an effect to avoid an extra render).
  const selectPeriodMode = () => {
    setDayMode('period');
    setPeriod('last30');
  };
  const selectSingleMode = () => {
    setDayMode('single');
    setSingleDate(SINGLE_DAY_OPTIONS[0]?.value ?? '');
  };

  const scopeNote = (
    <p className="academic-scores__scope">
      <i className="pi pi-info-circle" />
      <span>
        <strong>Scope:</strong> {scopeText}
      </span>
    </p>
  );

  const periodControl = (
    <div className="academic-scores__ctrl">
      <span className="academic-scores__ctrl-label">PERIOD</span>
      <Dropdown
        value={period}
        options={periodOptions}
        onChange={(e) => setPeriod(e.value)}
        className="academic-scores__dd"
      />
    </div>
  );

  // Shown on the Numbers tab when "Single day" is selected.
  const dateControl = (
    <div className="academic-scores__ctrl">
      <span className="academic-scores__ctrl-label">DATE</span>
      <Dropdown
        value={singleDate}
        options={SINGLE_DAY_OPTIONS}
        onChange={(e) => setSingleDate(e.value)}
        className="academic-scores__dd"
        filter
      />
    </div>
  );

  // ═════════════════════════════════════════════
  // NUMBERS
  // ═════════════════════════════════════════════
  const renderNumbers = () => {
    const bandColumns: ReportColumn[] = [
      { key: 'band', label: 'Attendance Band', type: 'swatch', bold: true },
      { key: 'schools', label: 'Schools', type: 'number' },
      {
        key: 'shareSchools',
        label: 'Share of Schools',
        type: 'text',
        align: 'right',
      },
      { key: 'students', label: 'Students', type: 'number' },
      {
        key: 'shareStudents',
        label: 'Share of Students',
        type: 'text',
        align: 'right',
      },
    ];

    const schoolTypeColumns: ReportColumn[] = [
      { key: 'type', label: 'School Type', type: 'text', bold: true },
      { key: 'schools', label: 'Schools', type: 'number' },
      { key: 'students', label: 'Students', type: 'number' },
      { key: 'attendance', label: 'Attendance', type: 'text', align: 'right' },
      { key: 'compliance', label: 'Compliance', type: 'text', align: 'right' },
      { key: 'teacher', label: 'Teacher', type: 'text', align: 'right' },
      {
        key: 'dropoutRisk',
        label: 'Dropout Risk',
        type: 'text',
        align: 'right',
      },
    ];

    const periodColumns: ReportColumn[] = [
      { key: 'period', label: 'Period', type: 'text', bold: true },
      { key: 'attendance', label: 'Attendance', type: 'text', align: 'right' },
      { key: 'absenteeDays', label: 'Absentee-Days', type: 'number' },
      { key: 'compliance', label: 'Compliance', type: 'text', align: 'right' },
      { key: 'unmarkedDays', label: 'Unmarked Days', type: 'number' },
    ];

    const districtColumns: ReportColumn[] = [
      { key: 'name', label: 'Name', type: 'link' },
      // Higher is better: green >= 90%, red < 85%.
      {
        key: 'attendance',
        label: 'Attendance',
        type: 'threshold',
        align: 'right',
        thresholds: { good: 90, bad: 85 },
      },
      {
        key: 'compliance',
        label: 'Compliance',
        type: 'threshold',
        align: 'right',
        thresholds: { good: 90, bad: 85 },
      },
      {
        key: 'teacher',
        label: 'Teacher',
        type: 'threshold',
        align: 'right',
        thresholds: { good: 90, bad: 85 },
      },
      { key: 'absenteeDays', label: 'Absentee-Days', type: 'number' },
      // Lower is better: green <= 1%, red > 5%.
      {
        key: 'dropoutRisk',
        label: 'Dropout Risk',
        type: 'threshold',
        align: 'right',
        thresholds: { good: 1, bad: 5 },
        invert: true,
      },
      // Head-count behind the Dropout Risk % that follows it.
      { key: 'dropouts', label: 'Potential Dropout Students', type: 'number' },
    ];

    const summaryFields: DrillField[] = [
      { key: 'name', type: 'name' },
      { key: 'attendance', type: 'pct' },
      // compliance / dropoutRisk are not in attendancedailytrend, so they are
      // 'No data'. Keep them as text so the drill-down filter nudging does not
      // turn 'No data' into '0.0%'.
      { key: 'compliance', type: 'text' },
      { key: 'teacher', type: 'pct' },
      { key: 'absenteeDays', type: 'count' },
      { key: 'dropoutRisk', type: 'text' },
      // A head-count, so the drill-down fallback splits it across children
      // the same way it splits absentee-days.
      { key: 'dropouts', type: 'count' },
    ];
    // The level the selection implies: none → districts, district → blocks,
    // district + block → schools.
    const selectedLevel: DrillLevel = !district ? 0 : !block ? 1 : 2;

    // Prefer the API's own rows whenever it already returned the level we are
    // showing — their names are the real district / block / school names, so
    // clicking a row sets a value the header dropdowns can actually display.
    // buildDrilldown is only the fallback for when the API has no child rows
    // for this scope (it synthesises plausible ones from the parent).
    const useApiRows = apiRowLevel != null && apiRowLevel === selectedLevel;
    const drill = useApiRows
      ? {
          level: selectedLevel,
          // A school picked in the header narrows the school list to that row.
          rows:
            selectedLevel === 2 && school
              ? districtBaseRows.filter((r) => String(r.name ?? '') === school)
              : districtBaseRows,
        }
      : buildDrilldown({
          baseRows: districtBaseRows,
          fields: summaryFields,
          district,
          block,
          school,
          filters,
        });

    const nameLabel = drillLevelLabel(drill.level);
    const summaryTitle = school
      ? `School — ${school}`
      : drill.level === 2
        ? `Schools — ${block}`
        : drill.level === 1
          ? `Blocks — ${district}`
          : 'Districts — Attendance Summary';
    districtColumns[0] = {
      key: 'name',
      label: nameLabel,
      // 'link' at every level: district/block rows drill deeper, a school row
      // (the leaf) opens its School Detail view.
      type: 'link',
    };
    // Row click → push the name up to Dashboard's handleDistrictChange /
    // handleBlockChange / setHeaderSchool, which is what keeps the header
    // dropdowns (and the breadcrumb trail) in sync with the table.
    const handleSummaryRowClick = (row: Record<string, unknown>) => {
      const name = String(row.name ?? '');
      if (!name) return;
      if (drill.level === 0) onDistrictChange?.(name);
      else if (drill.level === 1) onBlockChange?.(name);
      else if (drill.level === 2) selectSchool(name); // leaf → select the school
    };

    const distributionSeries: ChartSeries[] = [
      {
        label: 'Schools',
        data: bandRows.map((b) => Number(b.schools)),
        color: '#8bc34a',
        barColors: bandRows.map((b) => b.color),
      },
      {
        label: 'Students',
        data: bandRows.map((b) => Number(b.students)),
        color: '#8bc34a',
        barColors: bandRows.map((b) => b.color),
      },
    ];

    // On-bar labels: full school count on top, share % below (e.g. ["4,417",
    // "26.7%"]) — matches the distribution reference design.
    const distValueLabels: string[][] = bandRows.map((b) => [
      fmtIndian(Number(b.schools)),
      String(b.shareSchools),
    ]);

    // Distribution summary derived from the actual bands (no hardcoded text).
    const distTotal = bandRows.reduce(
      (t, b) => t + (Number(b.schools) || 0),
      0
    );
    const distBelow85 = bandRows.reduce((t, b) => {
      const m = String(b.band).match(/\d+/);
      const n = m ? Number(m[0]) : NaN;
      return t + (!Number.isNaN(n) && n < 85 ? Number(b.schools) || 0 : 0);
    }, 0);
    const distBelowPct =
      distTotal > 0 ? ((distBelow85 / distTotal) * 100).toFixed(1) : '0';
    const distSuffix = `${fmtIndian(distBelow85)} schools below 85% (${distBelowPct}%)`;

    const schoolTypeLabels = schoolTypeTop
      ? schoolTypeTop.map((s) => s.school_type)
      : SCHOOL_TYPE_CHART.types;

    const schoolTypeSeries: ChartSeries[] = schoolTypeTop
      ? [
          {
            label: 'Attendance',
            data: schoolTypeTop.map((s) => s.student_attendance_pct),
            color: '#2f5fb0',
          },
          {
            label: 'Marking Compliance',
            data: schoolTypeTop.map((s) => s.marking_compliance_pct),
            color: '#2e7d32',
          },
          {
            label: 'Teacher',
            data: schoolTypeTop.map((s) => s.teacher_attendance_pct),
            color: '#9b59b6',
          },
        ]
      : [
          {
            label: 'Attendance',
            data: SCHOOL_TYPE_CHART.studentAttendance,
            color: '#2f5fb0',
          },
          {
            label: 'Marking Compliance',
            data: SCHOOL_TYPE_CHART.markingCompliance,
            color: '#2e7d32',
          },
          {
            label: 'Teacher',
            data: SCHOOL_TYPE_CHART.teacherAttendance,
            color: '#9b59b6',
          },
        ];

    // School-type table rows (compliance / dropout not in this payload → "—").
    const schoolTypeRows = schoolTypeTop
      ? schoolTypeTop.map((s) => ({
          type: s.school_type,
          schools: Number(s.schools),
          students: Number(s.student_present) + Number(s.student_absent),
          attendance: `${s.student_attendance_pct}%`,
          compliance: `${s.marking_compliance_pct}%`,
          teacher: `${s.teacher_attendance_pct}%`,
          dropoutRisk: `${s.dropout_risk_pct}%`,
          dropouts: `${potential_dropouts}`,
        }))
      : SCHOOL_TYPES;

    // Rich hover tooltip for the School Type chart — all details per type.
    const schoolTypeTooltip: Record<
      string,
      { label: string; value: string }[]
    > = {};
    schoolTypeRows.forEach((s) => {
      schoolTypeTooltip[s.type] = [
        { label: 'Schools', value: fmtIndian(Number(s.schools)) },
        { label: 'Students', value: fmtIndian(Number(s.students)) },
        { label: 'Attendance', value: String(s.attendance) },
        { label: 'Compliance', value: String(s.compliance) },
        { label: 'Teacher', value: String(s.teacher) },
        { label: 'Dropout risk', value: String(s.dropoutRisk) },
      ];
    });

    const markedPct = Number(studentCompliansePct) || 0;
    const unmarkedPct = Number((100 - markedPct).toFixed(2));
    // School-day counts behind the marked/unmarked split: schools_marked and
    // unmarked_schools are per-day, so multiply by the selected working days.
    const days = selected_days || 1;
    const markedCount = Number.isFinite(schoolsVal)
      ? Math.round(schoolsVal * days)
      : undefined;
    const unmarkedCount = Number.isFinite(unmarked_schools)
      ? Math.round(unmarked_schools)
      : undefined;
    const markedSlices: ChartSlice[] = [
      {
        label: 'Marked',
        value: markedPct,
        color: '#2e7d32',
        count: markedCount,
      },
      {
        label: 'Unmarked',
        value: unmarkedPct,
        color: '#c0392b',
        count: unmarkedCount,
      },
    ];
    // Present/absent student counts from the summary (fall back to undefined so
    // the table shows "—" only when the API truly omits them).
    const presentCount = Number.isFinite(presentStudents)
      ? presentStudents
      : undefined;
    const absentCount = Number.isFinite(absentStudents)
      ? absentStudents
      : undefined;
    const presentSlices: ChartSlice[] = [
      {
        label: 'Present',
        value: presentPct,
        color: '#8bc34a',
        count: presentCount,
      },
      {
        label: 'Absent',
        value: absentPct,
        color: '#e4572e',
        count: absentCount,
      },
    ];

    // Built from the same API source (acrossList) as periodsTooltip, with
    // dummy fallback until the data loads.
    const periodsSeries: ChartSeries[] = [
      {
        label: 'Attendance %',
        data: periodAttendance,
        color: '#2f5fb0',
        asLine: true,
      },
      {
        label: 'Absentee-days',
        data: periodAbsentee,
        color: '#e4572e',
        asLine: true,
        useRightAxis: true,
      },
    ];

    return (
      <>
        <SectionTitle
          icon="pi pi-users"
          text={`Attendance — ${scopeName}`}
          suffix={workingDaysLabel}
        />

        <div className="row g-3 mb-3">
          <div className="col-6 col-lg-3">
            <StatTile
              icon="pi pi-check-circle"
              value={`${studentCompliansePct}%`}
              // value="No_Data"
              label="Marking Compliance"
            />
          </div>
          <div className="col-6 col-lg-3">
            <StatTile
              icon="pi pi-users"
              value={`${studentAttendancePct}%`}
              label="Student Attendance"
            />
          </div>
          <div className="col-6 col-lg-3">
            <StatTile
              icon="pi pi-user"
              value={`${teacherAttendancePct}%`}
              label="Teacher Attendance"
            />
          </div>
          <div className="col-6 col-lg-3">
            <StatTile
              icon="pi pi-exclamation-triangle"
              value={`${studentPotentialDropoutRiskPct}%`}
              // value="no_data"
              label="Dropout Risk"
            />
          </div>
        </div>

        <div className="row g-3 mb-4">
          <div className="col-6 col-lg">
            <MetricCard
              value={fmtIndian(schoolsVal)}
              label="Schools"
              loading={summaryLoading}
            />
          </div>
          <div className="col-6 col-lg">
            <MetricCard
              value={fmtIndian(totalStudents)}
              label="Students Enrolled"
              loading={summaryLoading}
            />
          </div>
          <div className="col-6 col-lg">
            <MetricCard
              value={fmtIndian(absenteeDays)}
              label={`Absentee-Days (${periodSuffix})`}
              accent="#e4572e"
              loading={summaryLoading}
            />
          </div>
          <div className="col-6 col-lg">
            <MetricCard
              value={fmtIndian(unmarked_schools)}
              label={`Unmarked School-Days (${periodSuffix})`}
              accent="#e8a33d"
              loading={summaryLoading}
            />
          </div>
          <div className="col-6 col-lg">
            <MetricCard
              value={fmtIndian(potential_dropouts)}
              label="Potential Dropouts (15+ Days Absent)"
              accent="#c0392b"
              loading={summaryLoading}
            />
          </div>
        </div>

        <div className="row g-3 mb-4">
          <div className="col-12 col-lg-6">
            <AppChart
              type="bar"
              title="School Attendance Distribution"
              titleSuffix={distSuffix}
              icon="pi pi-chart-bar"
              iconColor="#2f5fb0"
              labels={bandRows.map((b) => b.band)}
              series={distributionSeries}
              loading={bandsLoading}
              xTitle="Attendance band"
              yTitle="Schools"
              compactY
              showValueLabels
              valueLabels={distValueLabels}
              showLegend={false}
              height={320}
            />
          </div>
          <div className="col-12 col-lg-6">
            <AppChart
              type="bar"
              title="Attendance by School Type"
              titleSuffix="only types in scope"
              icon="pi pi-building"
              iconColor="#2e7d32"
              labels={schoolTypeLabels}
              series={schoolTypeSeries}
              loading={schoolTypeLoading}
              tooltipDetails={schoolTypeTooltip}
              yTitle="%"
              yTickSuffix="%"
              showValueLabels
              legendPosition="bottom"
              height={320}
            />
          </div>
        </div>

        <div className="mb-4">
          <ReportDataTable
            title={`Attendance Distribution — ${scopeName}`}
            hint="how many schools sit in each band"
            icon="pi pi-table"
            columns={bandColumns}
            data={bandRows}
            loading={bandsLoading}
            searchable={false}
            paginated={false}
            exportFileName="attendance-bands"
            rows={10}
          />
        </div>

        <div className="mb-4">
          <ReportDataTable
            title={`Attendance by School Type — ${scopeName}`}
            icon="pi pi-table"
            columns={schoolTypeColumns}
            data={schoolTypeRows}
            loading={schoolTypeLoading}
            searchable={false}
            paginated={false}
            exportFileName="attendance-school-types"
            rows={10}
          />
        </div>

        <div className="row g-3 mb-4">
          <div className="col-12 col-lg-4">
            <AppChart
              type="doughnut"
              title="Marked vs Unmarked School-Days"
              icon="pi pi-book"
              iconColor="#2e7d32"
              slices={markedSlices}
              loading={summaryLoading}
              height={340}
            />
          </div>
          <div className="col-12 col-lg-4">
            <AppChart
              type="doughnut"
              title="Students Present vs Absent"
              icon="pi pi-users"
              iconColor="#8bc34a"
              slices={presentSlices}
              loading={summaryLoading}
              height={340}
            />
          </div>
          <div className="col-12 col-lg-4">
            <AppChart
              type="combo"
              title="Attendance Across Periods"
              icon="pi pi-calendar"
              iconColor="#2f5fb0"
              labels={periodLabels}
              series={periodsSeries}
              loading={acrossLoading}
              tooltipDetails={periodsTooltip}
              yTitle="Percent"
              yTickSuffix="%"
              y1Title="Absentee-days"
              tools={chartToolsCombo}
              showValueLabels
              height={300}
            />
          </div>
        </div>

        <div className="mb-4">
          <ReportDataTable
            title={`Period Breakdown — ${scopeName}`}
            icon="pi pi-table"
            columns={periodColumns}
            data={periodBreakdownRows}
            loading={acrossLoading}
            searchable={false}
            paginated={false}
            exportFileName="attendance-periods"
            rows={10}
          />
        </div>

        <div className="mb-4">
          <DrillBreadcrumb
            district={district}
            block={block}
            school={school}
            onDistrictChange={onDistrictChange}
            onBlockChange={onBlockChange}
            onSchoolChange={onSchoolChange}
          />
          <ReportDataTable
            key={`${district ?? 'all'}-${block ?? ''}-${school ?? ''}`}
            title={`${summaryTitle} (${drill.rows.length})`}
            hint={drillHint(drill.level)}
            icon="pi pi-table"
            columns={districtColumns}
            data={drill.rows}
            loading={numbersLoading}
            searchPlaceholder={drillSearchPlaceholder(drill.level)}
            exportFileName="attendance-summary"
            rows={15}
            rowsPerPageOptions={[15, 30, 50]}
            onRowClick={handleSummaryRowClick}
          />
        </div>
      </>
    );
  };

  // ═════════════════════════════════════════════
  // TREND ANALYSIS
  // ═════════════════════════════════════════════
  const renderTrend = () => {
    // One row per working day from the attendancedailytrend API.
    type DailyTrendRow = {
      date?: string;
      edate?: string;
      att_date?: string;
      student_attendance_pct?: number | string;
      teacher_attendance_pct?: number | string;
      marked_days?: number | string;
      unmarked_days?: number | string;
    };
    const trendsData: DailyTrendRow[] = Array.isArray(
      (dailyTrendData as { trend?: DailyTrendRow[] })?.trend
    )
      ? (dailyTrendData as { trend: DailyTrendRow[] }).trend
      : [];
    const hasTrend = trendsData.length > 0;

    // X-axis labels from the API rows (fallback to dummy dates until loaded).
    const trendLabels = hasTrend
      ? trendsData.map((t) => t.date ?? t.edate ?? t.att_date ?? '')
      : DAILY_TREND.dates;

    // for trends1 — API values when available, else dummy fallback.
    // Combo (bar + line): Student Attendance as bars, Marking Compliance and
    // Teacher Attendance overlaid as lines. The Bar view toolbar toggle drops
    // the lines to grouped bars; Table View lists every day's values.
    const dailySeries: ChartSeries[] = hasTrend
      ? [
          {
            label: 'Student Attendance',
            data: trendsData.map((t) => Number(t.student_attendance_pct) || 0),
            color: '#2f5fb0',
          },
          {
            label: 'Marking Compliance',
            data: trendsData.map((t) => {
              const marked = Number(t.marked_days) || 0;
              const unmarked = Number(t.unmarked_days) || 0;
              const denom = marked + unmarked;
              return denom ? Math.round((marked / denom) * 10000) / 100 : 0;
            }),
            color: '#e4572e',
            asLine: true,
          },
          {
            label: 'Teacher Attendance',
            data: trendsData.map((t) => Number(t.teacher_attendance_pct) || 0),
            color: '#5cb85c',
            asLine: true,
          },
        ]
      : [
          {
            label: 'Student Attendance',
            data: DAILY_TREND.studentAttendance,
            color: '#2f5fb0',
          },
          {
            label: 'Marking Compliance',
            data: DAILY_TREND.markingCompliance,
            color: '#e4572e',
            asLine: true,
          },
          {
            label: 'Teacher Attendance',
            data: DAILY_TREND.teacherAttendance,
            color: '#5cb85c',
            asLine: true,
          },
        ];

    // ── "Lowest / Highest 12 Districts" line chart from MetricTrendData ──
    // days_data: one entry per working day { edate, groups[] }; each group is
    // one district { district_id, name, value }. Pivot into one line per
    // district (value across all days), then take the lowest/highest 12.
    type MetricGroup = {
      name: string;
      value: number | string;
      [key: string]: unknown;
    };
    type MetricDay = { edate: string; groups?: MetricGroup[] };
    const metricTrend = MetricTrendData as
      | {
          metric_name?: string;
          group_key?: string;
          level?: string;
          days_data?: MetricDay[];
        }
      | undefined;
    const daysData = Array.isArray(metricTrend?.days_data)
      ? metricTrend!.days_data!
      : [];
    const hasMetricTrend = daysData.length > 0;
    const metricName = metricTrend?.metric_name ?? 'Student Attendance';
    const groupKey = metricTrend?.group_key ?? 'district_id';
    // Entity noun follows the drill level returned by the API:
    // state/no selection → districts, district selected → blocks,
    // block selected → schools.
    const LEVEL_NOUN: Record<string, string> = {
      state: 'Districts',
      district: 'Districts',
      block: 'Blocks',
      school: 'Schools',
    };
    const levelNoun =
      LEVEL_NOUN[metricTrend?.level ?? 'district'] ?? 'Districts';

    const avg = (v: number[]) =>
      v.length ? v.reduce((s, x) => s + x, 0) / v.length : 0;

    // X-axis dates (fallback to dummy until loaded).
    const districtDates = hasMetricTrend
      ? daysData.map((d) => d.edate)
      : DAILY_TREND.dates;

    // Pivot: entity id → its value for each day (index-aligned to daysData).
    const seriesMap = new Map<string, { name: string; data: number[] }>();
    daysData.forEach((day, dayIdx) => {
      (day.groups ?? []).forEach((g) => {
        const id = String(g[groupKey] ?? g.name);
        if (!seriesMap.has(id)) {
          seriesMap.set(id, {
            name: g.name,
            data: new Array(daysData.length).fill(0),
          });
        }
        seriesMap.get(id)!.data[dayIdx] = Number(g.value) || 0;
      });
    });

    const DISTRICT_PALETTE = [
      '#c0392b',
      '#e4572e',
      '#e8a33d',
      '#f0c419',
      '#8bc34a',
      '#2f5fb0',
      '#5cb85c',
      '#7b1e3a',
      '#7e57c2',
      '#17a2b8',
      '#e4842c',
      '#546e7a',
    ];

    const districtSeries: ChartSeries[] = hasMetricTrend
      ? // A selected school → just that one school's line (in sync with the
        // header), otherwise the Lowest/Highest N entities.
        narrowToSchool(Array.from(seriesMap.values()), (e) => e.name)
          .sort((a, b) =>
            trendMode === 'lowest'
              ? avg(a.data) - avg(b.data)
              : avg(b.data) - avg(a.data)
          )
          .slice(0, trendShow)
          .map((e, i) => ({
            label: e.name,
            data: e.data,
            color: DISTRICT_PALETTE[i % DISTRICT_PALETTE.length],
          }))
      : [...LOWEST_DISTRICTS]
          .sort((a, b) =>
            trendMode === 'lowest'
              ? avg(a.values) - avg(b.values)
              : avg(b.values) - avg(a.values)
          )
          .map((d) => ({ label: d.name, data: d.values, color: d.color }));
    // ── Empty-state detection (once the API has responded) ──
    // A trend needs ≥2 days to draw a line; also treat an all-zero response
    // (nothing marked for the period) as empty.
    const allZero = (s: ChartSeries[]) =>
      s.every((ser) => (ser.data as number[]).every((v) => !Number(v)));
    const dailyLoaded = dailyTrendData != null;
    const metricLoaded = MetricTrendData != null;
    const dailyEmptyMsg =
      dailyLoaded && (trendsData.length < 2 || allZero(dailySeries))
        ? 'No trend data for the selected period. Try a longer period or a date range with marked attendance.'
        : undefined;
    const metricEmptyMsg =
      metricLoaded && (daysData.length < 2 || allZero(districtSeries))
        ? `No ${metricName} data for the selected period. Try a longer period or a different metric.`
        : undefined;

    // Built from the same API source (acrossList) as periodsTooltip, with
    // dummy fallback until the data loads.
    const periodsSeries: ChartSeries[] = [
      {
        label: 'Attendance %',
        data: periodAttendance,
        color: '#2f5fb0',
        asLine: true,
      },
      {
        label: 'Absentee-days',
        data: periodAbsentee,
        color: '#e4572e',
        asLine: true,
        useRightAxis: true,
      },
    ];

    return (
      <>
        <div className="academic-scores__controls">
          {periodControl}
          <div className="academic-scores__ctrl">
            <span className="academic-scores__ctrl-label">METRIC</span>
            <Dropdown
              value={trendMetric}
              options={metricOptions}
              onChange={(e) => setTrendMetric(e.value)}
              className="academic-scores__dd"
            />
          </div>
          <div
            className="academic-scores__band-toggle academic-scores__ml-auto"
            role="tablist"
          >
            <button
              type="button"
              className={`academic-scores__band${trendMode === 'lowest' ? ' is-active' : ''}`}
              onClick={() => setTrendMode('lowest')}
            >
              <i className="pi pi-arrow-down-right" /> Lowest
            </button>
            <button
              type="button"
              className={`academic-scores__band${trendMode === 'highest' ? ' is-active' : ''}`}
              onClick={() => setTrendMode('highest')}
            >
              <i className="pi pi-arrow-up-right" /> Highest
            </button>
          </div>
          <div className="academic-scores__ctrl">
            <span className="academic-scores__ctrl-label">SHOW</span>
            <Dropdown
              value={trendShow}
              options={showOptions}
              onChange={(e) => setTrendShow(e.value)}
              className="academic-scores__dd"
            />
          </div>
          {scopeNote}
        </div>

        <SectionTitle
          icon="pi pi-chart-line"
          text={`Attendance Trend — ${scopeName}`}
          suffix={workingDaysLabel}
        />

        <DrillBreadcrumb
          district={district}
          block={block}
          school={school}
          onDistrictChange={onDistrictChange}
          onBlockChange={onBlockChange}
          onSchoolChange={onSchoolChange}
        />

        <div className="mb-4">
          <AppChart
            type="combo"
            title="Daily Attendance, Compliance & Teacher Attendance"
            icon="pi pi-chart-bar"
            iconColor="#2f5fb0"
            labels={trendLabels}
            series={dailySeries}
            loading={dailyLoading}
            yTitle="Percent"
            yTickSuffix="%"
            emptyMessage={dailyEmptyMsg}
            tools={chartToolsCombo}
            height={340}
          />
        </div>

        <div className="mb-4">
          <AppChart
            type="bar"
            title={`${trendMode === 'lowest' ? 'Lowest' : 'Highest'} ${districtSeries.length} ${levelNoun} — ${metricName}`}
            titleSuffix="click a bar to drill down"
            icon="pi pi-chart-bar"
            iconColor="#e4842c"
            labels={districtDates}
            series={districtSeries}
            // Page the DATE axis only, 6 days at a time. With SHOW set high
            // (up to 38 districts) a 15-day window packed hundreds of bars
            // into one frame; 6 days keeps each day's group readable.
            labelsPerPage={6}
            // And never page the DISTRICTS: the legend lists all of them at
            // once so a district can be found (and toggled) without clicking
            // through legend pages. It wraps onto a few rows instead.
            paginateLegend={false}
            loading={metricLoading}
            yTitle="Students"
            yTickSuffix="%"
            tooltipSort="desc"
            emptyMessage={metricEmptyMsg}
            tools={chartToolsCombo}
            height={360}
          />
        </div>

        <div className="mb-4">
          <AppChart
            type="combo"
            title="Attendance Across Periods (3 / 5 / 7 / 15 / 30 days)"
            icon="pi pi-calendar"
            iconColor="#2f5fb0"
            labels={periodLabels}
            series={periodsSeries}
            tooltipDetails={periodsTooltip}
            yTitle="Percent"
            yTickSuffix="%"
            y1Title="Absentee-days"
            tools={chartToolsCombo}
            showValueLabels
            height={340}
          />
        </div>
      </>
    );
  };

  // ═════════════════════════════════════════════
  // COMPARISON
  // ═════════════════════════════════════════════
  const renderComparison = () => {
    // ── Comparison data (attendancecomparison) ──
    // Districts mode: one aggregated value per district. Days mode: day-wise
    // groups (grouped by district/block/school per the header selection).
    const cmp = (ComparisonData ?? {}) as Record<string, unknown>;
    const metricId = Number(comparisonMetric) || 1;
    const metricName = String(cmp.metric_name ?? 'Student Attendance');

    // The drilled level reuses whichever array the API returns.
    const cmpDistrictsAll = Array.isArray(cmp.districts)
      ? (cmp.districts as Record<string, unknown>[])
      : Array.isArray(cmp.blocks)
        ? (cmp.blocks as Record<string, unknown>[])
        : Array.isArray(cmp.schools)
          ? (cmp.schools as Record<string, unknown>[])
          : Array.isArray(cmp.groups)
            ? (cmp.groups as Record<string, unknown>[])
            : [];
    // A selected school → just that school's bar/row (in sync with the header).
    const cmpDistricts = narrowToSchool(cmpDistrictsAll, (d) => d.name);

    // Bars: API already sorts lowest-first; 12 highest coloured green.
    const barItems = cmpDistricts
      .map((d) => ({ name: String(d.name ?? ''), value: Number(d.value) }))
      .filter((d) => d.name && !Number.isNaN(d.value));
    const barDistrictsRaw = barItems.length
      ? barItems
      : ATTENDANCE_BARS.map((d) => ({ name: d.name, value: d.value }));
    // Sort descending so the highest values (green / at-or-above state) sit at
    // the TOP and the lowest (red / below state) at the bottom — green → red
    // top-to-bottom.
    const barDistricts = [...barDistrictsRaw].sort((a, b) => b.value - a.value);

    // Colour each bar by the state average: at/above = green, below = red.
    const stateAvg = barDistricts.length
      ? barDistricts.reduce((t, d) => t + d.value, 0) / barDistricts.length
      : 0;
    const totalGroups = Number(cmp.group_count) || barDistricts.length;
    const barSeries: ChartSeries[] = [
      {
        label: metricName,
        data: barDistricts.map((d) => d.value),
        color: '#c0392b',
        barColors: barDistricts.map((d) =>
          d.value >= stateAvg ? '#2e7d32' : '#c0392b'
        ),
      },
    ];
    const barTooltip: Record<string, { label: string; value: string }[]> =
      Object.fromEntries(
        barDistricts.map((d) => [
          d.name,
          [{ label: metricName, value: `${d.value}%` }],
        ])
      );

    // Metric-specific table columns + row mapper (count fields vary by metric).
    const metricColumns: Record<number, ReportColumn[]> = {
      1: [
        { key: 'name', label: 'District', type: 'link' },
        { key: 'value', label: 'Attendance %', type: 'text' },
        { key: 'present', label: 'Present', type: 'number' },
        { key: 'absent', label: 'Absent', type: 'number' },
        { key: 'total', label: 'Total Students', type: 'number' },
      ],
      2: [
        { key: 'name', label: 'District', type: 'link' },
        { key: 'value', label: 'Compliance %', type: 'text' },
        { key: 'marked', label: 'Marked Schools', type: 'number' },
        { key: 'unmarked', label: 'Unmarked Schools', type: 'number' },
      ],
      3: [
        { key: 'name', label: 'District', type: 'link' },
        { key: 'value', label: 'Teacher Attendance %', type: 'text' },
        { key: 'present', label: 'Teachers Present', type: 'number' },
        { key: 'absent', label: 'Teachers Absent', type: 'number' },
        { key: 'total', label: 'Total Teachers', type: 'number' },
      ],
      4: [
        { key: 'name', label: 'District', type: 'link' },
        { key: 'value', label: 'Dropout Risk %', type: 'text' },
        { key: 'dropouts', label: 'Potential Dropouts', type: 'number' },
        { key: 'total', label: 'Total Students', type: 'number' },
      ],
    };
    // Drill level — prefer the API's reported level, else infer from the
    // header geography (district_id -> blocks, block_id -> schools).
    const apiLevel = String(cmp.level ?? '');
    const level =
      apiLevel ||
      (blockId != null && blockId !== ''
        ? 'school'
        : districtId != null && districtId !== ''
          ? 'block'
          : 'district');
    const atSchoolLevel = level === 'school';
    const atBlockLevel = level === 'block';
    const canDrill = !atSchoolLevel; // schools are the leaf level
    const levelNoun = atSchoolLevel
      ? 'School'
      : atBlockLevel
        ? 'Block'
        : 'District';
    const comparisonChartTitle = atSchoolLevel
      ? `${metricName} by School — ${block}`
      : atBlockLevel
        ? `${metricName} by Block — ${district}`
        : `${metricName} by District`;
    // Clicking a bar/row drills via the header filters (district -> block ->
    // school); attendancecomparison re-fires with the new id.
    const drillByName = (name: string) => {
      if (!name) return;
      if (atSchoolLevel) {
        selectSchool(name); // school = leaf → select the school
        return;
      }
      if (atBlockLevel) onBlockChange?.(name);
      else onDistrictChange?.(name);
    };
    const districtColumns = [...(metricColumns[metricId] ?? metricColumns[1])];
    districtColumns[0] = {
      key: 'name',
      label: levelNoun,
      type: canDrill ? 'link' : 'text',
    };
    const districtTableRows: Record<string, unknown>[] = cmpDistricts.map(
      (d) => {
        const base = { name: ndText(d.name), value: ndPct(d.value) };
        if (metricId === 2)
          return {
            ...base,
            marked: ndNum(d.marked_schools),
            unmarked: ndNum(d.unmarked_schools),
          };
        if (metricId === 3)
          return {
            ...base,
            present: ndNum(d.teacher_present),
            absent: ndNum(d.teacher_absent),
            total: ndNum(d.total_teachers),
          };
        if (metricId === 4)
          return {
            ...base,
            dropouts: ndNum(d.potential_dropouts),
            total: ndNum(d.total_students),
          };
        return {
          ...base,
          present: ndNum(d.student_present),
          absent: ndNum(d.student_absent),
          total: ndNum(d.total_students),
        };
      }
    );

    // ── Full Comparison: merge all metrics per district ──────────
    // Each metric endpoint returns one value + its own counts per district;
    // merge them by name into a single all-metrics row (NAME, ENROLLED,
    // ATTENDANCE, COMPLIANCE, UNMARKED, TEACHER, AT RISK).
    const rowsOf = (d: unknown): Record<string, unknown>[] => {
      const o = (d ?? {}) as Record<string, unknown>;
      const arr = o.districts ?? o.blocks ?? o.schools ?? o.groups;
      return Array.isArray(arr) ? (arr as Record<string, unknown>[]) : [];
    };
    const byName = (rows: Record<string, unknown>[]) => {
      const m: Record<string, Record<string, unknown>> = {};
      rows.forEach((r) => {
        const n = String(r.name ?? '');
        if (n) m[n] = r;
      });
      return m;
    };
    const attRows = narrowToSchool(rowsOf(FullAttendance), (r) => r.name);
    const compMap = byName(rowsOf(FullCompliance));
    const teachMap = byName(rowsOf(FullTeacher));
    const dropMap = byName(rowsOf(FullDropout));
    const fullRows: Record<string, unknown>[] = attRows.map((a) => {
      const n = String(a.name ?? '');
      const comp = compMap[n] ?? {};
      const teach = teachMap[n] ?? {};
      const drop = dropMap[n] ?? {};
      const atRiskCount = drop.potential_dropouts;
      const atRiskPct = drop.value;
      return {
        name: ndText(a.name),
        enrolled: ndNum(a.total_students),
        attendance: ndPct(a.value),
        compliance: ndPct(comp.value),
        unmarked: ndNum(comp.unmarked_schools),
        teacher: ndPct(teach.value),
        atRisk:
          atRiskCount == null || atRiskCount === ''
            ? '0'
            : `${Number(atRiskCount).toLocaleString('en-IN')}${
                atRiskPct != null && atRiskPct !== '' ? ` (${atRiskPct}%)` : ''
              }`,
      };
    });
    const fullColumns: ReportColumn[] = [
      { key: 'name', label: levelNoun, type: 'link' },
      { key: 'enrolled', label: 'Enrolled', type: 'number' },
      {
        key: 'attendance',
        label: 'Attendance',
        type: 'threshold',
        align: 'right',
        thresholds: { good: 90, bad: 85 },
      },
      {
        key: 'compliance',
        label: 'Compliance',
        type: 'threshold',
        align: 'right',
        thresholds: { good: 90, bad: 85 },
      },
      { key: 'unmarked', label: 'Unmarked', type: 'number' },
      {
        key: 'teacher',
        label: 'Teacher',
        type: 'threshold',
        align: 'right',
        thresholds: { good: 90, bad: 85 },
      },
      { key: 'atRisk', label: 'At Risk', type: 'text', align: 'right' },
    ];
    const fullTitle = `Full Comparison${
      dayMode === 'single' ? ` — ${singleDate}` : ''
    }`;

    // ── Between Days: compare two selected days across groups ──
    const daysData = Array.isArray(cmp.days_data)
      ? (cmp.days_data as Record<string, unknown>[])
      : [];
    const dayDates = daysData.map((d) => String(d.edate ?? ''));
    const dayDateOptions = dayDates.map((d) => ({ label: d, value: d }));
    const levelLabel = String(cmp.level ?? 'district');

    const groupsOf = (
      data: unknown,
      date: string | null
    ): Record<string, unknown>[] => {
      const dd = (data as { days_data?: unknown })?.days_data;
      const day = (
        Array.isArray(dd) ? (dd as Record<string, unknown>[]) : []
      ).find((d) => String(d.edate ?? '') === date);
      const groups = (day as { groups?: unknown } | undefined)?.groups;
      return Array.isArray(groups) ? (groups as Record<string, unknown>[]) : [];
    };

    // Grouped bars (Day A vs Day B) + difference line, for the selected metric.
    // A selected school → just that school's groups (in sync with the header),
    // so the chart shows a single entity instead of every school in the block.
    const aGroups = narrowToSchool(groupsOf(cmp, dayA), (g) => g.name);
    const bGroups = narrowToSchool(groupsOf(cmp, dayB), (g) => g.name);
    const dayGroupNames: string[] = [];
    [...aGroups, ...bGroups].forEach((g) => {
      const n = String(g.name ?? '');
      if (n && !dayGroupNames.includes(n)) dayGroupNames.push(n);
    });
    const valMap = (groups: Record<string, unknown>[]) => {
      const m = new Map<string, number>();
      groups.forEach((g) => m.set(String(g.name ?? ''), Number(g.value)));
      return m;
    };
    const aValMap = valMap(aGroups);
    const bValMap = valMap(bGroups);
    const dayASeriesData = dayGroupNames.map((n) => {
      const v = aValMap.get(n);
      return v == null || Number.isNaN(v) ? NaN : v;
    });
    const dayBSeriesData = dayGroupNames.map((n) => {
      const v = bValMap.get(n);
      return v == null || Number.isNaN(v) ? NaN : v;
    });
    const dayDiffData = dayGroupNames.map((n) => {
      const a = aValMap.get(n);
      const b = bValMap.get(n);
      return a == null || b == null || Number.isNaN(a) || Number.isNaN(b)
        ? NaN
        : Number((b - a).toFixed(2));
    });
    const daysSeries: ChartSeries[] = [
      { label: dayA ?? 'Day A', data: dayASeriesData, color: '#2f5fb0' },
      { label: dayB ?? 'Day B', data: dayBSeriesData, color: '#8bc34a' },
      {
        label: 'Difference (B − A)',
        data: dayDiffData,
        color: '#e4842c',
        asLine: true,
        useRightAxis: true,
      },
    ];

    // Summary cards: aggregate attendance / compliance / teacher per day.
    const sumField = (groups: Record<string, unknown>[], key: string): number =>
      groups.reduce((t, g) => t + (Number(g[key]) || 0), 0);
    const pct1 = (num: number, den: number): number | null =>
      den > 0 ? Math.round((num / den) * 1000) / 10 : null;
    const dayCard = (date: string | null) => {
      // A selected school → aggregate ONLY that school's groups, so the cards
      // report the single school's figures instead of the whole block's.
      const att = narrowToSchool(groupsOf(DaysAttendance, date), (g) => g.name);
      const com = narrowToSchool(groupsOf(DaysCompliance, date), (g) => g.name);
      const tea = narrowToSchool(groupsOf(DaysTeacher, date), (g) => g.name);
      const present = sumField(att, 'student_present');
      const absent = sumField(att, 'student_absent');
      const marked = sumField(com, 'marked_schools');
      const unmarked = sumField(com, 'unmarked_schools');
      const tPresent = sumField(tea, 'teacher_present');
      const tAbsent = sumField(tea, 'teacher_absent');
      return {
        attendance: pct1(present, present + absent),
        compliance: pct1(marked, marked + unmarked),
        teacher: pct1(tPresent, tPresent + tAbsent),
        schoolsMarked: marked,
        studentsAbsent: absent,
      };
    };
    const cardA = dayCard(dayA);
    const cardB = dayCard(dayB);
    const pctText = (v: number | null) => (v == null ? '0' : `${v}%`);
    const diffText = (a: number | null, b: number | null) =>
      a == null || b == null
        ? '0'
        : `${b - a >= 0 ? '+' : ''}${(b - a).toFixed(1)}%`;

    // ── Day-over-Day Movement grid ──────────────────────────────
    // Per-entity Day A vs Day B attendance, with the change in BOTH attendance
    // and compliance. The two metrics come from separate sections of the same
    // response (DaysAttendance / DaysCompliance), joined here by entity name.
    const ddAttA = valMap(groupsOf(DaysAttendance, dayA));
    const ddAttB = valMap(groupsOf(DaysAttendance, dayB));
    const ddCompA = valMap(groupsOf(DaysCompliance, dayA));
    const ddCompB = valMap(groupsOf(DaysCompliance, dayB));

    // Entity list from the attendance metric, Day A first then anything that
    // only appears on Day B.
    const ddNamesAll: string[] = [];
    [
      ...groupsOf(DaysAttendance, dayA),
      ...groupsOf(DaysAttendance, dayB),
    ].forEach((g) => {
      const n = String(g.name ?? '');
      if (n && !ddNamesAll.includes(n)) ddNamesAll.push(n);
    });
    // A selected school → just that school across both days (in sync).
    const ddNames = narrowToSchool(ddNamesAll, (n) => n);

    const r1 = (v: number) => Math.round(v * 10) / 10;
    /** Change between the two days, or null when either day has no figure. */
    const deltaOf = (
      a: Map<string, number>,
      b: Map<string, number>,
      name: string
    ): number | null => {
      const av = a.get(name);
      const bv = b.get(name);
      if (av == null || bv == null || Number.isNaN(av) || Number.isNaN(bv)) {
        return null;
      }
      return r1(bv - av);
    };

    const dayMovementRows: Record<string, unknown>[] = ddNames
      .map((name) => ({
        name,
        // Day columns are plain text so a missing day reads "No data" rather
        // than being coerced to 0%.
        dayAPct: ddAttA.get(name) == null ? '0' : `${ddAttA.get(name)}%`,
        dayBPct: ddAttB.get(name) == null ? '0' : `${ddAttB.get(name)}%`,
        attChange: deltaOf(ddAttA, ddAttB, name),
        compChange: deltaOf(ddCompA, ddCompB, name),
      }))
      // Biggest attendance DROP first — that is what needs attention. Rows
      // with no change figure sort last.
      .sort((x, y) => {
        const a = x.attChange;
        const b = y.attChange;
        if (a == null) return 1;
        if (b == null) return -1;
        return a - b;
      });

    const dayMovementColumns: ReportColumn[] = [
      {
        key: 'name',
        label: levelNoun,
        type: 'link',
      },
      { key: 'dayAPct', label: dayA ?? 'Day A', type: 'text', align: 'right' },
      { key: 'dayBPct', label: dayB ?? 'Day B', type: 'text', align: 'right' },
      // `percent` renders an up/down arrow + signed value, green for a rise
      // and red for a fall.
      {
        key: 'attChange',
        label: 'Attendance Change',
        type: 'percent',
        align: 'right',
      },
      // Same colouring, no arrow — one arrow column per grid is enough.
      {
        key: 'compChange',
        label: 'Compliance Change',
        type: 'percent',
        align: 'right',
        hideArrow: true,
      },
    ];

    // ── Between Years (attendanceyearcomparison) ──
    const yc = (YearComparisonData ?? {}) as Record<string, unknown>;
    const ycYearA = (yc.year_a ?? {}) as Record<string, unknown>;
    const ycYearB = (yc.year_b ?? {}) as Record<string, unknown>;
    const ycDiff = (yc.difference ?? {}) as Record<string, unknown>;
    const yearItemsAll = Array.isArray(yc.items)
      ? (yc.items as Record<string, unknown>[])
      : [];
    // A selected school → EXACTLY that school's row, resolved ONCE here and
    // reused by the cards, the chart AND the table below, so all three always
    // agree. narrowToSchool() (used elsewhere in this file) silently falls
    // back to the FULL unfiltered list whenever its name match comes up
    // empty — that's harmless for a chart/table (it just shows the whole
    // block instead of one bar), but it meant the cards (which separately
    // required an exact single-row match) and the chart/table could each
    // reach a different conclusion about whether a school was actually
    // resolved, so they drifted out of sync.
    const stripUdise = (s: string) =>
      s
        .replace(/\s*\(\d[\d/]*\)\s*$/, '')
        .trim()
        .toLowerCase();
    const yearSchoolMatch = school
      ? (yearItemsAll.find(
          (r) => stripUdise(String(r.name ?? '')) === stripUdise(school)
        ) as Record<string, unknown> | undefined)
      : undefined;
    // When a school is selected, show ONLY its matched row — never silently
    // fall back to the full list, so the chart/table can't show "every
    // school in the block" while the cards (below) show one school's figures.
    const yearItems = school
      ? yearSchoolMatch
        ? [yearSchoolMatch]
        : []
      : yearItemsAll;

    const yearLabelOf = (id: number | null) =>
      yearOptions.find((o) => o.value === id)?.label ?? String(id ?? '');
    const yearALabel = String(ycYearA.academic_year ?? yearLabelOf(fromYear));
    const yearBLabel = String(ycYearB.academic_year ?? yearLabelOf(toYear));
    // Card titles include the selected month in brackets when months are set.
    const monthLabelOf = (v: number | null) =>
      v == null
        ? ''
        : (MONTH_OPTIONS.find((o) => o.value === v)?.label ?? String(v));
    const cardATitle = `${yearALabel}${
      monthFrom != null ? ` (${monthLabelOf(monthFrom)})` : ''
    }`;
    const cardBTitle = `${yearBLabel}${
      monthTo != null ? ` (${monthLabelOf(monthTo)})` : ''
    }`;
    const ndNumOrNull = (v: unknown): number | null => {
      const n = Number(v);
      return v == null || v === '' || Number.isNaN(n) ? null : n;
    };

    // Grouped bars per entity (Year A vs Year B) + attendance-change line.
    const yearGroupNames = yearItems.map((r) => String(r.name ?? ''));
    const yearAData = yearItems.map(
      (r) => ndNumOrNull(r.year_a_attendance_pct) ?? NaN
    );
    const yearBData = yearItems.map(
      (r) => ndNumOrNull(r.year_b_attendance_pct) ?? NaN
    );
    const yearDiffData = yearItems.map(
      (r) => ndNumOrNull(r.attendance_change) ?? NaN
    );
    const yearSeries: ChartSeries[] = [
      { label: yearALabel, data: yearAData, color: '#2f5fb0' },
      { label: yearBLabel, data: yearBData, color: '#8bc34a' },
      {
        label: 'Difference (B − A)',
        data: yearDiffData,
        color: '#e4842c',
        asLine: true,
        useRightAxis: true,
      },
    ];

    // Overall summary cards. attendanceyearcomparison's top-level year_a /
    // year_b / difference aggregates are only ever scoped by district_id /
    // block_id (yearArgs never sends school_id — there is no per-school
    // aggregate endpoint) — so once a school is selected, those three objects
    // still describe the whole BLOCK, not the one school. Source the cards
    // from the SAME yearSchoolMatch row the chart + table use above, instead
    // of the block-level aggregate.
    const yearSchoolRow = yearSchoolMatch ?? null;
    const yearAAtt = yearSchoolRow
      ? ndNumOrNull(yearSchoolRow.year_a_attendance_pct)
      : ndNumOrNull(ycYearA.attendance_pct);
    const yearBAtt = yearSchoolRow
      ? ndNumOrNull(yearSchoolRow.year_b_attendance_pct)
      : ndNumOrNull(ycYearB.attendance_pct);
    const yearAttChange = yearSchoolRow
      ? ndNumOrNull(yearSchoolRow.attendance_change)
      : ndNumOrNull(ycDiff.attendance_change);
    // Per-school absent/total/schools counts, same year_a_* / year_b_*
    // prefix convention as the pct/absent fields already read above.
    const yearAAbsent = yearSchoolRow
      ? Number(yearSchoolRow.year_a_students_absent) || 0
      : Number(ycYearA.students_absent) || 0;
    const yearBAbsent = yearSchoolRow
      ? Number(yearSchoolRow.year_b_students_absent) || 0
      : Number(ycYearB.students_absent) || 0;
    const yearATotalStudents = yearSchoolRow
      ? Number(yearSchoolRow.year_a_total_students) || 0
      : Number(ycYearA.total_students) || 0;
    const yearBTotalStudents = yearSchoolRow
      ? Number(yearSchoolRow.year_b_total_students) || 0
      : Number(ycYearB.total_students) || 0;
    // A single selected school is itself exactly 1 school in both years.
    const yearASchools = yearSchoolRow ? 1 : Number(ycYearA.schools) || 0;
    const yearBSchools = yearSchoolRow ? 1 : Number(ycYearB.schools) || 0;
    const yPct = (v: number | null) => (v == null ? '0' : `${v}%`);
    const changeText = (v: number | null) =>
      v == null ? '0' : `${v >= 0 ? '+' : ''}${v}%`;

    // Drill level (district -> block -> school) from the header selection.
    const yearAtSchool = blockId != null && blockId !== '';
    const yearAtBlock =
      !yearAtSchool && districtId != null && districtId !== '';
    const yearCanDrill = !yearAtSchool;
    const yearLevelNoun = yearAtSchool
      ? 'School'
      : yearAtBlock
        ? 'Block'
        : 'District';
    const yearDrill = (name: string) => {
      if (!name) return;
      if (yearAtSchool) {
        selectSchool(name); // school = leaf → select the school
        return;
      }
      if (yearAtBlock) onBlockChange?.(name);
      else onDistrictChange?.(name);
    };

    // Bottom grid: entity-wise year_a vs year_b with change + absentees.
    const yearComparisonColumns: ReportColumn[] = [
      {
        key: 'name',
        label: yearLevelNoun,
        type: 'link',
      },
      { key: 'yearA', label: `${yearALabel} %`, type: 'text' },
      { key: 'yearB', label: `${yearBLabel} %`, type: 'text' },
      { key: 'change', label: 'Change', type: 'text' },
      { key: 'absentA', label: `${yearALabel} Absent`, type: 'number' },
      { key: 'absentB', label: `${yearBLabel} Absent`, type: 'number' },
    ];
    const yearComparisonRows: Record<string, unknown>[] = yearItems.map(
      (r) => ({
        name: String(r.name ?? ''),
        yearA: yPct(ndNumOrNull(r.year_a_attendance_pct)),
        yearB: yPct(ndNumOrNull(r.year_b_attendance_pct)),
        change: changeText(ndNumOrNull(r.attendance_change)),
        absentA: Number(r.year_a_students_absent) || 0,
        absentB: Number(r.year_b_students_absent) || 0,
      })
    );

    return (
      <>
        <div className="academic-scores__band-toggle mb-3" role="tablist">
          <button
            type="button"
            className={`academic-scores__band${comparisonView === 'districts' ? ' is-active' : ''}`}
            onClick={() => setComparisonView('districts')}
          >
            <i className="pi pi-building" /> Between Districts
          </button>
          <button
            type="button"
            className={`academic-scores__band${comparisonView === 'days' ? ' is-active' : ''}`}
            onClick={() => setComparisonView('days')}
          >
            <i className="pi pi-calendar" /> Between Days
          </button>
          <button
            type="button"
            className={`academic-scores__band${comparisonView === 'years' ? ' is-active' : ''}`}
            onClick={() => setComparisonView('years')}
          >
            <i className="pi pi-calendar" /> Between years
          </button>
        </div>

        <div className="academic-scores__controls">
          {comparisonView === 'years' ? (
            <>
              <div className="academic-scores__controls">
                <div className="academic-scores__ctrl">
                  <span className="academic-scores__ctrl-label">YEAR A</span>
                  <Dropdown
                    value={fromYear}
                    options={yearAOptions}
                    onChange={(e) => setFromYear(e.value)}
                    placeholder="Select year"
                    className="academic-scores__dd"
                  />
                </div>
                <div className="academic-scores__ctrl">
                  <span className="academic-scores__ctrl-label">YEAR B</span>
                  <Dropdown
                    value={toYear}
                    options={yearBOptions}
                    onChange={(e) => setToYear(e.value)}
                    placeholder="Select year"
                    className="academic-scores__dd"
                  />
                </div>
              </div>
              {bothYearsSelected && (
                <div className="academic-scores__controls">
                  <div className="academic-scores__ctrl">
                    <span className="academic-scores__ctrl-label">
                      MONTH ({yearALabel})
                    </span>
                    <Dropdown
                      value={monthFrom}
                      options={yearMonthOptions}
                      onChange={(e) => setMonthFrom(e.value)}
                      placeholder={
                        monthTo != null && monthFrom == null
                          ? 'Required'
                          : 'Full year'
                      }
                      showClear
                      className="academic-scores__dd"
                    />
                  </div>
                  <div className="academic-scores__ctrl">
                    <span className="academic-scores__ctrl-label">
                      MONTH ({yearBLabel})
                    </span>
                    <Dropdown
                      value={monthTo}
                      options={yearMonthOptions}
                      onChange={(e) => setMonthTo(e.value)}
                      placeholder={
                        monthFrom != null && monthTo == null
                          ? 'Required'
                          : 'Full year'
                      }
                      showClear
                      className="academic-scores__dd"
                    />
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="academic-scores__mode-group">
              <div className="academic-scores__band-toggle" role="tablist">
                {/* <button
                  type="button"
                  className={`academic-scores__band${dayMode === 'period' ? ' is-active' : ''}`}
                  onClick={selectPeriodMode}
                >
                  <i className="pi pi-calendar" /> Period
                </button> */}
                {/* <button
                  type="button"
                  className={`academic-scores__band${dayMode === 'single' ? ' is-active' : ''}`}
                  onClick={selectSingleMode}
                >
                  <i className="pi pi-calendar-plus" /> Single day
                </button> */}
              </div>
              {dayMode === 'single' ? dateControl : periodControl}
              <div className="academic-scores__ctrl">
                <span className="academic-scores__ctrl-label">METRIC</span>
                <Dropdown
                  value={comparisonMetric}
                  options={metricOptions}
                  onChange={(e) => setComparisonMetric(e.value)}
                  className="academic-scores__dd"
                />
              </div>
            </div>
          )}
          {scopeNote}
        </div>

        <SectionTitle
          icon="pi pi-arrows-h"
          text={`Attendance Comparison — ${scopeName}`}
          suffix={
            comparisonView === 'years'
              ? 'academic year comparison'
              : workingDaysLabel
          }
        />

        {comparisonView === 'districts' && (
          <>
            <DrillBreadcrumb
              district={district}
              block={block}
              school={school}
              onDistrictChange={onDistrictChange}
              onBlockChange={onBlockChange}
              onSchoolChange={onSchoolChange}
            />
            <div className="mb-4">
              <AppChart
                type="horizontalBar"
                title={comparisonChartTitle}
                titleSuffix={
                  canDrill
                    ? '— click a bar to drill down'
                    : '— click a bar to select a school'
                }
                titleExtra={
                  <span className="attendance-rank-legend">
                    <span className="attendance-rank-legend__item">
                      <span
                        className="attendance-rank-legend__dot"
                        style={{ backgroundColor: '#c0392b' }}
                      />
                      below state
                    </span>
                    <span className="attendance-rank-legend__item">
                      <span
                        className="attendance-rank-legend__dot"
                        style={{ backgroundColor: '#2e7d32' }}
                      />
                      at or above state
                    </span>
                  </span>
                }
                icon="pi pi-chart-bar"
                iconColor="#c0392b"
                headerRight={
                  <span className="attendance-count-badge">
                    {totalGroups} {levelNoun}
                    {totalGroups === 1 ? '' : 's'}
                  </span>
                }
                labels={barDistricts.map((d) => d.name)}
                series={barSeries}
                loading={comparisonLoading}
                yTitle=""
                xTickSuffix="%"
                showLegend={false}
                averageLine={stateAvg}
                barValueFormat={(v) => `${v}%`}
                tooltipDetails={barTooltip}
                // Always clickable — like the table's onRowClick below.
                // drillByName drills district → block → school, and at the
                // schools-list (leaf) level it selects that single school
                // instead of drilling further.
                onBarClick={(_i, label) => drillByName(label)}
                tools={chartToolsCombo}
                height={420}
              />
            </div>

            <div className="mb-4">
              {/* Repeated next to the grid that drills — the breadcrumb at the
                  top of this view sits above the ranked bar chart. */}
              <DrillBreadcrumb
                district={district}
                block={block}
                onDistrictChange={onDistrictChange}
                onBlockChange={onBlockChange}
                onSchoolChange={onSchoolChange}
              />
              <ReportDataTable
                key={`${district ?? 'all'}-${block ?? ''}-${school ?? ''}`}
                title={`${fullTitle} (${fullRows.length} records)`}
                suffix={workingDaysLabel}
                hint={canDrill ? drillHint(levelNoun) : ''}
                icon="pi pi-table"
                // columns={fullColumns}
                columns={
                  school && fullRows.length === 1
                    ? [
                        { ...fullColumns[0], type: 'text' },
                        ...fullColumns.slice(1),
                      ]
                    : fullColumns
                }
                data={fullRows}
                loading={comparisonLoading}
                searchPlaceholder={drillSearchPlaceholder(levelNoun)}
                exportFileName="attendance-full-comparison"
                rows={15}
                rowsPerPageOptions={[15, 30, 50]}
                onRowClick={(row) => drillByName(String(row.name ?? ''))}
              />
            </div>
          </>
        )}

        {comparisonView === 'days' && (
          <>
            <div className="academic-scores__controls1">
              <div className="academic-scores__ctrl">
                <span className="academic-scores__ctrl-label">DAY A</span>
                <Dropdown
                  value={dayA}
                  options={dayDateOptions}
                  onChange={(e) => setDayA(e.value)}
                  placeholder="Select day"
                  filter
                  className="academic-scores__dd"
                />
              </div>
              <span className="academic-scores__vs">vs</span>
              <div className="academic-scores__ctrl">
                <span className="academic-scores__ctrl-label">DAY B</span>
                <Dropdown
                  value={dayB}
                  options={dayDateOptions}
                  onChange={(e) => setDayB(e.value)}
                  placeholder="Select day"
                  filter
                  className="academic-scores__dd"
                />
              </div>
            </div>

            <DrillBreadcrumb
              district={district}
              block={block}
              school={school}
              onDistrictChange={onDistrictChange}
              onBlockChange={onBlockChange}
              onSchoolChange={onSchoolChange}
            />

            <div className="row g-3 mb-4">
              <div className="col-12 col-md-5">
                <div className="card p-3 h-100">
                  <h6 className="mb-3">{dayA ?? 'Day A'}</h6>
                  <div className="d-flex justify-content-between py-1">
                    <span className="report-dt__muted">Attendance</span>
                    <strong>{pctText(cardA.attendance)}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-1">
                    <span className="report-dt__muted">Compliance</span>
                    <strong>{pctText(cardA.compliance)}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-1">
                    <span className="report-dt__muted">Teacher</span>
                    <strong>{pctText(cardA.teacher)}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-1">
                    <span className="report-dt__muted">Schools Marked</span>
                    <strong>{fmtIndian(cardA.schoolsMarked)}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-1">
                    <span className="report-dt__muted">Students Absent</span>
                    <strong>{fmtIndian(cardA.studentsAbsent)}</strong>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-2">
                <div className="card p-3 h-100 text-center d-flex flex-column justify-content-center">
                  <span className="academic-scores__ctrl-label">
                    DIFFERENCE (B - A)
                  </span>
                  <div className="mt-2">
                    <strong>
                      {diffText(cardA.attendance, cardB.attendance)}
                    </strong>
                  </div>
                  <small className="report-dt__muted">attendance</small>
                  <div className="mt-2">
                    <strong>
                      {diffText(cardA.compliance, cardB.compliance)}
                    </strong>
                  </div>
                  <small className="report-dt__muted">compliance</small>
                </div>
              </div>
              <div className="col-12 col-md-5">
                <div className="card p-3 h-100">
                  <h6 className="mb-3">{dayB ?? 'Day B'}</h6>
                  <div className="d-flex justify-content-between py-1">
                    <span className="report-dt__muted">Attendance</span>
                    <strong>{pctText(cardB.attendance)}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-1">
                    <span className="report-dt__muted">Compliance</span>
                    <strong>{pctText(cardB.compliance)}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-1">
                    <span className="report-dt__muted">Teacher</span>
                    <strong>{pctText(cardB.teacher)}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-1">
                    <span className="report-dt__muted">Schools Marked</span>
                    <strong>{fmtIndian(cardB.schoolsMarked)}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-1">
                    <span className="report-dt__muted">Students Absent</span>
                    <strong>{fmtIndian(cardB.studentsAbsent)}</strong>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <AppChart
                type="combo"
                title={`${levelLabel}s — ${metricName} : (${dayA} vs ${dayB})`}
                titleSuffix="dashed line = change (B - A)"
                icon="pi pi-chart-bar"
                iconColor="#2f5fb0"
                labels={dayGroupNames}
                series={daysSeries}
                loading={comparisonLoading}
                labelsPerPage={15}
                yTitle="Percent"
                yTickSuffix="%"
                y1Title="Difference"
                showValueLabels
                tools={chartToolsCombo}
                height={440}
              />
            </div>

            {/* Day-over-Day Movement grid — the main table for this view. */}
            <div className="mb-4">
              <DrillBreadcrumb
                district={district}
                block={block}
                school={school}
                onDistrictChange={onDistrictChange}
                onBlockChange={onBlockChange}
                onSchoolChange={onSchoolChange}
              />
              <ReportDataTable
                key={`daymove-${district ?? 'all'}-${block ?? ''}-${school ?? ''}-${dayA ?? ''}-${dayB ?? ''}`}
                title={`Day-over-Day Movement by ${levelNoun}s`}
                hint={canDrill ? drillHint(levelNoun) : ''}
                icon="pi pi-table"
                // columns={dayMovementColumns}
                columns={
                  school && dayMovementRows.length === 1
                    ? [
                        { ...dayMovementColumns[0], type: 'text' },
                        ...dayMovementColumns.slice(1),
                      ]
                    : dayMovementColumns
                }
                data={dayMovementRows}
                loading={comparisonLoading}
                searchPlaceholder={drillSearchPlaceholder(levelNoun)}
                exportFileName="attendance-day-over-day-movement"
                rows={15}
                rowsPerPageOptions={[15, 30, 50]}
                onRowClick={(row) => drillByName(String(row.name ?? ''))}
              />
            </div>
          </>
        )}

        {comparisonView === 'years' && (
          <>
            <DrillBreadcrumb
              district={district}
              block={block}
              school={school}
              onDistrictChange={onDistrictChange}
              onBlockChange={onBlockChange}
              onSchoolChange={onSchoolChange}
            />
            <div className="row g-3 mb-4">
              <div className="col-12 col-md-5">
                <div className="card p-3 h-100">
                  <h6 className="mb-3">{cardATitle}</h6>
                  <div className="d-flex justify-content-between py-1">
                    <span className="report-dt__muted">Attendance</span>
                    <strong>{yPct(yearAAtt)}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-1">
                    <span className="report-dt__muted">Students Absent</span>
                    <strong>{fmtIndian(yearAAbsent)}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-1">
                    <span className="report-dt__muted">Total Students</span>
                    <strong>{fmtIndian(yearATotalStudents)}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-1">
                    <span className="report-dt__muted">Schools</span>
                    <strong>{fmtIndian(yearASchools)}</strong>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-2">
                <div className="card p-3 h-100 text-center d-flex flex-column justify-content-center">
                  <span className="academic-scores__ctrl-label">
                    DIFFERENCE (B − A)
                  </span>
                  <div className="mt-2">
                    <strong>{changeText(yearAttChange)}</strong>
                  </div>
                  <small className="report-dt__muted">attendance</small>
                </div>
              </div>
              <div className="col-12 col-md-5">
                <div className="card p-3 h-100">
                  <h6 className="mb-3">{cardBTitle}</h6>
                  <div className="d-flex justify-content-between py-1">
                    <span className="report-dt__muted">Attendance</span>
                    <strong>{yPct(yearBAtt)}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-1">
                    <span className="report-dt__muted">Students Absent</span>
                    <strong>{fmtIndian(yearBAbsent)}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-1">
                    <span className="report-dt__muted">Total Students</span>
                    <strong>{fmtIndian(yearBTotalStudents)}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-1">
                    <span className="report-dt__muted">Schools</span>
                    <strong>{fmtIndian(yearBSchools)}</strong>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <AppChart
                type="combo"
                title={`${yearLevelNoun}s — ${yearALabel} vs ${yearBLabel}`}
                titleSuffix="dashed line = change (B − A)"
                icon="pi pi-chart-bar"
                iconColor="#2f5fb0"
                labels={yearGroupNames}
                series={yearSeries}
                loading={yearLoading}
                yTitle="Percent"
                yTickSuffix="%"
                y1Title="Difference"
                onBarClick={
                  yearCanDrill ? (_i, label) => yearDrill(label) : undefined
                }
                tools={chartToolsCombo}
                height={440}
              />
            </div>

            <div className="mb-4">
              {/* Repeated next to the grid that drills — the breadcrumb at the
                  top of this view sits above the year comparison chart. */}
              <DrillBreadcrumb
                district={district}
                block={block}
                onDistrictChange={onDistrictChange}
                onBlockChange={onBlockChange}
                onSchoolChange={onSchoolChange}
              />
              <ReportDataTable
                key={`${district ?? 'all'}-${block ?? ''}-${school ?? ''}`}
                title={`Year Comparison — ${yearLevelNoun}s`}
                hint={yearCanDrill ? drillHint(yearLevelNoun) : ''}
                icon="pi pi-table"
                // columns={yearComparisonColumns}
                columns={
                  school && yearComparisonRows.length === 1
                    ? [
                        { ...yearComparisonColumns[0], type: 'text' },
                        ...yearComparisonColumns.slice(1),
                      ]
                    : yearComparisonColumns
                }
                data={yearComparisonRows}
                loading={yearLoading}
                searchPlaceholder={drillSearchPlaceholder(yearLevelNoun)}
                exportFileName="attendance-year-comparison"
                rows={15}
                rowsPerPageOptions={[15, 30, 50]}
                onRowClick={(row) => yearDrill(String(row.name ?? ''))}
              />
            </div>
          </>
        )}
      </>
    );
  };

  // ═════════════════════════════════════════════
  // ALARMING
  // ═════════════════════════════════════════════
  const renderAlarming = () => {
    const catPalette = ['#e8a33d', '#e4572e', '#8bc34a', '#7b1e3a'];
    const alertsSeries: ChartSeries[] = [
      {
        label: 'Districts',
        data: ATT_ALERTS_BY_CATEGORY.counts,
        color: '#e8a33d',
        barColors: ATT_ALERTS_BY_CATEGORY.counts.map(
          (_, i) => catPalette[i % catPalette.length]
        ),
      },
    ];

    const scatterGroups: ScatterGroup[] = Object.keys(RISK_COLORS)
      .map((g) => ({
        label: RISK_LABELS[g],
        color: RISK_COLORS[g],
        points: ATT_RISK_POINTS.filter((p) => p.group === g).map((p) => ({
          x: p.compliance,
          y: p.attendance,
          label: p.name,
        })),
      }))
      .filter((grp) => grp.points.length > 0);

    const dropoutSeries: ChartSeries[] = [
      {
        label: 'Absent 15+ days %',
        data: POTENTIAL_DROPOUT_BARS.map((d) => d.pct),
        color: '#e8a33d',
        barColors: POTENTIAL_DROPOUT_BARS.map((d) =>
          d.high ? '#e8a33d' : '#8bc34a'
        ),
      },
    ];

    const alertColumns: ReportColumn[] = [
      { key: 'severity', label: 'Severity', type: 'text' },
      // Only a link (and so only chevroned) when a drill handler is wired.
      {
        key: 'district',
        label: 'District',
        type: onDistrictChange ? 'link' : 'text',
      },
      { key: 'alert', label: 'Alert', type: 'text' },
      { key: 'measured', label: 'Measured', type: 'text' },
    ];
    // Row click → drill into that district (and sync the header dropdown).
    const alertDrill = onDistrictChange
      ? (row: Record<string, unknown>) => {
          const name = String(row.district ?? '');
          if (name) onDistrictChange(name);
        }
      : undefined;

    return (
      <>
        <div className="academic-scores__controls">
          {periodControl}
          {scopeNote}
        </div>

        <SectionTitle
          icon="pi pi-exclamation-triangle"
          text={`Attendance Alarming Indicators (${ATT_ALARMING_KPIS.totalAlerts}) — ${scopeName}`}
          suffix={workingDaysLabel}
        />

        <div className="row g-3 mb-4">
          <div className="col-6 col-lg-3">
            <StatTile
              className="ac-tile ac-tile--danger"
              value={`${ATT_ALARMING_KPIS.highSeverity}`}
              label="High Severity"
            />
          </div>
          <div className="col-6 col-lg-3">
            <StatTile
              className="ac-tile ac-tile--warning"
              value={`${ATT_ALARMING_KPIS.mediumSeverity}`}
              label="Medium Severity"
            />
          </div>
          <div className="col-6 col-lg-3">
            <StatTile
              className="ac-tile ac-tile--navy"
              value={`${ATT_ALARMING_KPIS.totalAlerts}`}
              label="Total Alerts"
            />
          </div>
          <div className="col-6 col-lg-3">
            <StatTile
              className="ac-tile ac-tile--muted"
              value={`${ATT_ALARMING_KPIS.districtsFlagged}`}
              label="Districts Flagged"
            />
          </div>
        </div>

        <div className="row g-3 mb-4">
          <div className="col-12 col-lg-5">
            <AppChart
              type="bar"
              title="Alerts by Category"
              icon="pi pi-chart-bar"
              iconColor="#e8a33d"
              labels={ATT_ALERTS_BY_CATEGORY.categories}
              series={alertsSeries}
              yTitle="Districts"
              tools={chartToolsNoTable}
              height={340}
            />
          </div>
          <div className="col-12 col-lg-7">
            <AppChart
              type="scatter"
              title="Compliance vs Attendance"
              titleSuffix="bottom-left = not marking AND low attendance"
              icon="pi pi-compass"
              iconColor="#2f5fb0"
              scatterGroups={scatterGroups}
              xTitle="Marking Compliance %"
              xTickSuffix="%"
              yTitle="Student Attendance %"
              yTickSuffix="%"
              tools={chartToolsNoTable}
              height={340}
            />
          </div>
        </div>

        <div className="mb-4">
          <AppChart
            type="horizontalBar"
            title="Potential Dropouts — absent 15+ days in the month"
            titleSuffix="click a bar to drill into the district"
            icon="pi pi-directions"
            iconColor="#e8a33d"
            labels={POTENTIAL_DROPOUT_BARS.map((d) => d.name)}
            series={dropoutSeries}
            yTitle=""
            xTickSuffix="%"
            tools={chartToolsNoTable}
            height={520}
          />
        </div>

        <div className="academic-scores__chips mb-3">
          {ATT_ALERT_CATEGORIES.map((c) => (
            <button
              key={c.key}
              type="button"
              className={`academic-scores__chip${alarmFilter === c.key ? ' is-active' : ''}`}
              onClick={() => setAlarmFilter(c.key)}
            >
              {c.label} ({c.count})
            </button>
          ))}
        </div>

        <div className="mb-4">
          <DrillBreadcrumb
            district={district}
            block={block}
            onDistrictChange={onDistrictChange}
            onBlockChange={onBlockChange}
            onSchoolChange={onSchoolChange}
          />
          <ReportDataTable
            title="Attendance Alert Details"
            hint={`thresholds: compliance < 80% · attendance < 85% · teacher < 90% · dropout risk > 5% ${
              alertDrill ? drillHint('district') : ''
            }`.trim()}
            icon="pi pi-list"
            columns={alertColumns}
            data={ATT_ALERT_DETAILS}
            searchPlaceholder={drillSearchPlaceholder('district')}
            exportFileName="attendance-alerts"
            rows={15}
            rowsPerPageOptions={[15, 30]}
            onRowClick={alertDrill}
          />
        </div>
      </>
    );
  };

  // ═════════════════════════════════════════════
  // CAUTION
  // ═════════════════════════════════════════════
  const renderCaution = () => {
    // Presentation (icon / accent / severity) per API tile `type`. The API
    // only returns type/key/title/description/schools, so these decorate it.
    // Presentation (icon / accent / severity) for each live tile.
    //
    // Single source of truth is ATT_CAUTION_CHECKS in
    // shared/json/attendance.ts — edit the icon there and it shows here.
    // The API tile carries `type`, `key` and `title`; its `key` strings do NOT
    // match our `key` field, so `type` is the reliable join and title is a
    // last-resort fallback.
    const normalise = (s: unknown) =>
      String(s ?? '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');

    const lookByType = new Map(
      ATT_CAUTION_CHECKS.map((c) => [String(c.type), c])
    );
    const lookByKey = new Map(
      ATT_CAUTION_CHECKS.map((c) => [normalise(c.key), c])
    );
    const lookByLabel = new Map(
      ATT_CAUTION_CHECKS.map((c) => [normalise(c.label), c])
    );

    const presentationFor = (t: {
      type?: unknown;
      key?: unknown;
      title?: unknown;
    }) => {
      const hit =
        lookByType.get(String(t.type)) ??
        lookByKey.get(normalise(t.key)) ??
        lookByLabel.get(normalise(t.title));
      return hit
        ? { icon: hit.icon, accent: hit.accent, severity: hit.severity }
        : {
            icon: 'pi pi-exclamation-triangle',
            accent: '#b8860b',
            severity: 'High' as const,
          };
    };

    // Normalise the API tiles onto the card/table shape the UI renders.
    // Falls back to the static checks until the API data arrives.
    const cautionSummary: typeof ATT_CAUTION_CHECKS = Array.isArray(
      CautionSummaryData?.tiles
    )
      ? CautionSummaryData.tiles.map(
          (t: {
            type: string;
            key: string;
            title: string;
            description: string;
            schools: string | number;
          }) => {
            const look = presentationFor(t);
            return {
              key: t.key,
              label: t.title,
              meaning: t.description,
              count: Number(t.schools) || 0,
              severity: look.severity,
              icon: look.icon,
              accent: look.accent,
            };
          }
        )
      : ATT_CAUTION_CHECKS;

    const activeCheck =
      cautionSummary.find((c) => c.key === cautionCheck) ?? cautionSummary[0];

    // Per-school rows for the active check (attendancecautionlist).
    // Map API field names onto the table columns; fall back to the static
    // list until the API responds.

    const cautionSchoolRows = narrowToSchool(
      Array.isArray(CautionListData?.schools)
        ? (CautionListData!.schools as Record<string, unknown>[])
        : [],
      (r) => r.school_name
    ).map((r) => ({
      // Unique row identity for the table. school_id is the DB primary
      // key, so it is always present and never collides (udise can be
      // blank for some rows, which breaks PrimeReact reconciliation).
      schoolId: String(r.school_id ?? r.udise_code ?? ''),
      district: r.district_name ?? '',
      block: r.block_name ?? '',
      school: withUdise(
        r.school_name as string,
        r.udise_code as string | number
      ),
      management: r.management ?? '',
      students: Number(r.students) || 0,
      finding: r.finding ?? '',
    }));
    //console.log(cautionSchoolRows, 'cautionSchoolRows');
    const schoolColumns: ReportColumn[] = [
      { key: 'district', label: 'District', type: 'text' },
      { key: 'block', label: 'Block', type: 'text' },
      // Schools are the LEAF of the drill — clicking one opens its detail
      // view. The UDISE is appended into the name, so there's no separate
      // UDISE column.
      { key: 'school', label: 'School', type: 'link' },
      { key: 'management', label: 'Management', type: 'text' },
      { key: 'students', label: 'Students', type: 'number' },
      { key: 'finding', label: 'Finding', type: 'text' },
    ];

    const checkColumns: ReportColumn[] = [
      // 'link' so ReportDataTable's link-cell renderer draws the row's own
      // `icon` field (set below from ATT_CAUTION_CHECKS / the API tile) before
      // the check name — e.g. fa-calendar-xmark for "Zero Marking Compliance".
      { key: 'label', label: 'Check', type: 'link', bold: true },
      { key: 'severity', label: 'Severity', type: 'badge' },
      { key: 'count', label: 'Schools', type: 'number' },
      { key: 'meaning', label: 'What It Means', type: 'text' },
    ];

    // Left-border colour per severity for the caution cards.
    const sevColor = (sv: string) =>
      sv === 'High' ? '#c0392b' : sv === 'Medium' ? '#b7791f' : '#1b7a34';

    return (
      <>
        <div className="academic-scores__controls">
          {periodControl}
          {scopeNote}
        </div>

        <SectionTitle
          icon="pi pi-info-circle"
          text={`Caution — ${scopeName}`}
          suffix={`${CautionSummaryData?.flagged_schools ?? ATT_CAUTION_TOTAL} schools flagged`}
        />

        <div className="academic-scores__banner mb-3">
          <i className="pi pi-exclamation-triangle" />
          <span>
            Schools failing a hard check — a facility, a figure or a submission
            that is zero — rather than merely scoring below average. Every list
            below downloads in full, school by school. Schools that never marked
            attendance contribute nothing to the attendance percentages shown
            elsewhere, so they are invisible outside this view.
          </span>
        </div>

        <div className="row g-3 mb-4">
          {cautionSummary.map((c: (typeof ATT_CAUTION_CHECKS)[number]) => (
            <div className="col-6 col-md-4 col-xl-2" key={c.key}>
              <button
                type="button"
                className={`academic-scores__caution-card${cautionCheck === c.key ? ' is-active' : ''}`}
                style={{ borderLeft: `4px solid ${sevColor(c.severity)}` }}
                onClick={() => setCautionCheck(c.key)}
              >
                <span
                  className="academic-scores__caution-icon"
                  style={{ color: c.accent }}
                >
                  {/* AppIcon, not a raw <i>: no FontAwesome webfont CSS is
                      loaded, so `fa-*` classes on an <i> render nothing.
                      AppIcon routes fa-* through <FontAwesomeIcon> and leaves
                      `pi pi-*` on an <i>, so either family works. */}
                  <AppIcon icon={c.icon} />
                </span>
                <span className="academic-scores__caution-count">
                  {c.count}
                </span>
                <span className="academic-scores__caution-label">
                  {c.label}
                </span>
                <span className="academic-scores__caution-meaning">
                  {c.meaning}
                </span>
              </button>
            </div>
          ))}
        </div>

        <div className="mb-4">
          <DrillBreadcrumb
            district={district}
            block={block}
            school={school}
            onDistrictChange={onDistrictChange}
            onBlockChange={onBlockChange}
            onSchoolChange={onSchoolChange}
          />
          <ReportDataTable
            key={activeCheck.key}
            dataKey="schoolId"
            title={activeCheck.label}
            hint={activeCheck.meaning}
            suffix={`(${cautionSchoolRows.length} Schools)`}
            icon={activeCheck.icon}
            columns={schoolColumns}
            data={cautionSchoolRows}
            loading={cautionListLoading}
            searchPlaceholder="Search school or UDISE..."
            exportFileName={`attendance-caution-${activeCheck.key}`}
            rows={15}
            rowsPerPageOptions={[15, 30]}
            // Rows ARE schools (the leaf) — clicking one jumps straight to
            // that school, syncing the header District/Block/School dropdowns
            // too (the row already carries its own district/block names) so
            // the breadcrumb above this grid appears and matches the pick.
            onRowClick={(row) =>
              onGeoSelect?.({
                district: String(row.district ?? '') || null,
                block: String(row.block ?? '') || null,
                school:
                  String(row.school ?? '')
                    .replace(/\s*\(\d[\d/]*\)\s*$/, '')
                    .trim() || null,
              })
            }
          />
        </div>

        <div className="mb-4 cautions-grid">
          <ReportDataTable
            title={`All Caution Checks — ${scopeName}`}
            hint="click a row to view that check"
            icon="pi pi-list"
            columns={checkColumns}
            data={cautionSummary}
            loading={cautionSummaryLoading}
            searchable={false}
            paginated={false}
            exportFileName="attendance-caution-checks"
            onRowClick={(row) => setCautionCheck(String(row.key))}
            isRowActive={(row) => String(row.key) === cautionCheck}
            rows={10}
          />
        </div>
      </>
    );
  };

  return (
    <section className="academic-scores p-3">
      {activeSubTab === 'numbers' && (
        <div className="academic-scores__controls">
          <div className="academic-scores__mode-group">
            <div className="academic-scores__band-toggle" role="tablist">
              {/* <button
                type="button"
                className={`academic-scores__band${dayMode === 'period' ? ' is-active' : ''}`}
                onClick={selectPeriodMode}
              >
                <i className="pi pi-calendar" /> Period
              </button> */}
              {/* <button
                type="button"
                className={`academic-scores__band${dayMode === 'single' ? ' is-active' : ''}`}
                onClick={selectSingleMode}
              >
                <i className="pi pi-calendar-plus" /> Single day
              </button> */}
            </div>
            {dayMode === 'single' ? dateControl : periodControl}
          </div>
          {scopeNote}
        </div>
      )}

      {activeSubTab === 'numbers' && renderNumbers()}
      {activeSubTab === 'trend' && renderTrend()}
      {activeSubTab === 'comparison' && renderComparison()}
      {/* {activeSubTab === 'alarming' && renderAlarming()} */}
      {activeSubTab === 'caution' && renderCaution()}
    </section>
  );
};

const SectionTitle: React.FC<{
  icon: string;
  text: string;
  suffix?: string;
}> = ({ icon, text, suffix }) => (
  <h5 className="academic-scores__heading">
    <i className={icon} />
    {text} {suffix && <span>{suffix}</span>}
  </h5>
);

export default Attendance;
